/**
 * Datos de prueba para desarrollo local con Dexie.
 * Capa 1 (jerarquia VD): Budget -> AFE -> Coste -> Distribucion mensual matricial.
 *
 * Doble proteccion contra duplicados (StrictMode ejecuta efectos 2x):
 *  1. Singleton promise: solo corre una vez por carga de pagina
 *  2. IDs deterministicos: si por alguna razon corre 2x, los bulkAdd
 *     fallan por colision de PK en lugar de duplicar
 *
 * Modelo: Budgets fiscales (5 anos: 2 atras + actual + 2 adelante), Catalogo de
 * 12 costes VD §7.12, AFEs nuevos con TRM congelada, costes con codigo y
 * matriz mensual. PR/PO se reseed en Capa 2 con la nueva estructura.
 */

import { localDb } from "./db";
import { CATEGORIAS_RESET } from "./resetCategorias";
import {
  activarModoOperacion,
  desactivarModoOperacion,
  estaEnModoOperacion,
} from "@core/data/modoOperacion";
import { CLASES_COSTE } from "@constants/catalogos";
import { leerContratosDefault } from "@features/contratos/contratosDefault";
import { leerAfesDefault } from "@features/afe/afesDefault";
import { leerSosDefault, leerTareasDefault } from "@features/so/soDefault";
import { leerRdsDefault } from "@features/rd/rdDefault";
import { ensureSeedRiesgos } from "@features/riesgos/riesgosSeed";
import { ensureSeedStakeholders } from "@features/stakeholders/stakeholdersSeed";
import { buildRepos } from "@core/data/repos";
import { congelarMonto } from "@/lib/moneda";
import { ESTADOS_SO } from "@constants/estados";
import type {
  Afe,
  AfeCosto,
  AfeCosteMensual,
  So,
  Tarea,
  Usuario,
  Evento,
  Rd,
  Acta,
  ContratoMarco,
  Budget,
  CosteCatalogo,
  Pr,
  Po,
  PrHistorialEntry,
  PoHistorialEntry,
  SoHistorialEntry,
  Mr,
} from "@app-types/entities";

const TENANT_LOCAL = "local-dev";

// IDs deterministicos para datos de seed
const SEED_IDS = {
  // Budgets (2 cerrados + activo + 1 futuro = 4)
  budget23_24: "seed-budget-23-24",
  budget24_25: "seed-budget-24-25",
  budget25_26: "seed-budget-25-26",
  budget26_27: "seed-budget-26-27",
  // AFEs
  afe1: "seed-afe-001",
  afe2: "seed-afe-002",
  afe3: "seed-afe-003",
  afe4: "seed-afe-004",
  afe5: "seed-afe-005",
  afe6: "seed-afe-006",
  afe7: "seed-afe-007",
  afe8: "seed-afe-008",
  // SOs
  so1: "seed-so-001",
  so2: "seed-so-002",
  so3: "seed-so-003",
  so4: "seed-so-004",
  so5: "seed-so-005",
  so6: "seed-so-006",
  so7: "seed-so-007",
  so8: "seed-so-008",
  so9: "seed-so-009",
  so10: "seed-so-010",
  so11: "seed-so-011",
  so12: "seed-so-012",
  so13: "seed-so-013",
  so14: "seed-so-014",
  so15: "seed-so-015",
  // SOs de Metálicas Brayan (contrato2) — cobertura completa de estados
  so16: "seed-so-016",
  so17: "seed-so-017",
  so18: "seed-so-018",
  so19: "seed-so-019",
  so20: "seed-so-020",
  so21: "seed-so-021",
  so22: "seed-so-022",
  so23: "seed-so-023",
  so24: "seed-so-024",
  so25: "seed-so-025",
  so26: "seed-so-026",
  so27: "seed-so-027",
  so28: "seed-so-028",
  so29: "seed-so-029",
  so30: "seed-so-030",
  // Tareas
  tarea1: "seed-tarea-001",
  tarea2: "seed-tarea-002",
  tarea3: "seed-tarea-003",
  // Usuarios
  user1: "seed-user-001",
  user2: "seed-user-002",
  user3: "seed-user-003",
  user4: "seed-user-004",
  user5: "seed-user-005",
  user6: "seed-user-006",
  user7: "seed-user-007",
  user8: "seed-user-008",
  // Eventos
  evento1: "seed-evento-001",
  evento2: "seed-evento-002",
  evento3: "seed-evento-003",
  evento4: "seed-evento-004",
  evento5: "seed-evento-005",
  evento6: "seed-evento-006",
  evento7: "seed-evento-007",
  // RDs
  rd1: "seed-rd-001",
  rd2: "seed-rd-002",
  rd3: "seed-rd-003",
  rd4: "seed-rd-004",
  // RDs adicionales para simular calificaciones medias por contrato marco.
  rd5: "seed-rd-005",
  rd6: "seed-rd-006",
  rd7: "seed-rd-007",
  // RDs de SOs Metálicas Brayan (so24-so28) — testing del módulo RD.
  rd8: "seed-rd-008",
  rd9: "seed-rd-009",
  rd10: "seed-rd-010",
  rd11: "seed-rd-011",
  rd12: "seed-rd-012",
  rd13: "seed-rd-013",
  rd14: "seed-rd-014",
  rd15: "seed-rd-015",
  // Actas
  acta1: "seed-acta-001",
  acta2: "seed-acta-002",
  // Contratos marco
  contrato1: "seed-contrato-001",
  contrato2: "seed-contrato-002",
  contrato3: "seed-contrato-003",
  contrato4: "seed-contrato-004",
};

let seedPromise: Promise<void> | null = null;

/**
 * Punto de entrada del seed central. Se llama desde `App.tsx` y desde
 * `LocalAuthProvider` al montar. Es NO-OP cuando el PMIS está en modo
 * operación — evita que los seeds resuciten tras un Reset Total.
 */
export function seedIfEmpty(): Promise<void> {
  if (estaEnModoOperacion()) {
    // En modo operación, NUNCA sembramos. El admin ya decidió que la base
    // queda como está. La promesa resuelta evita que los callers esperen.
    return Promise.resolve();
  }
  if (!seedPromise) seedPromise = doSeed();
  return seedPromise;
}

/**
 * Borra todas las tablas y vuelve a sembrar los datos de demostración.
 * Útil para demos / sustentaciones donde se quiere volver al estado canónico
 * después de jugar con los ejemplos. Operación destructiva — el caller debe
 * confirmar con el usuario antes de invocar.
 *
 * Esta acción VUELVE al modo demo aunque el admin hubiera activado modo
 * operación previamente (Reset Total). Es la salida explícita de operación.
 */
export async function resetSeedDemo(): Promise<void> {
  // PRIMERO desactivamos modo operación para que el seed pueda correr;
  // si no, `seedIfEmpty()` y los `ensureSeed*` quedarían bloqueados.
  desactivarModoOperacion();
  await wipeAllTables({ activarOperacion: false });
  // Forzamos una nueva ejecución del seed (el promise memoizado quedó stale).
  seedPromise = doSeed();
  await seedPromise;
}

/**
 * Borra el contenido de TODAS las tablas del PMIS sin volver a sembrar nada.
 * Deja la base local completamente en blanco — el operador empezará a cargar
 * los datos reales del proyecto desde cero. Es la operación destructiva más
 * peligrosa del PMIS (junto con `resetSeedDemo`) y por eso solo el ADMIN_PMIS
 * puede invocarla, con doble confirmación, desde el módulo Inicio.
 *
 * Por default ACTIVA el modo operación — bloqueando que los seeds resurjan
 * al navegar entre módulos o al recargar la página. Pase `activarOperacion:
 * false` solo cuando se llama desde `resetSeedDemo` (que reactivará seeds
 * inmediatamente).
 */
export async function wipeAllTables(
  opts: { activarOperacion?: boolean } = {},
): Promise<void> {
  const activarOp = opts.activarOperacion ?? true;
  await Promise.all([
    localDb.budgets.clear(),
    localDb.costesCatalogo.clear(),
    localDb.afes.clear(),
    localDb.sos.clear(),
    localDb.tareas.clear(),
    localDb.usuarios.clear(),
    localDb.eventos.clear(),
    localDb.rds.clear(),
    localDb.actas.clear(),
    localDb.contratosMarco.clear(),
    localDb.tarifas.clear(),
    localDb.prs.clear(),
    localDb.pos.clear(),
    localDb.mrs.clear(),
    localDb.auditLog.clear(),
    localDb.configuracionesNotificacion.clear(),
    localDb.listasDistribucion.clear(),
    localDb.notificacionesEnviadas.clear(),
    localDb.configuracionesIA.clear(),
    localDb.riesgos.clear(),
    localDb.stakeholders.clear(),
    localDb.catalogosRiesgos.clear(),
    localDb.riskBurndownSnapshots.clear(),
    localDb.listadosPendientes.clear(),
    localDb.pendientesComm.clear(),
    localDb.sistemasComm.clear(),
    localDb.subsistemasComm.clear(),
    localDb.mejoras.clear(),
    localDb.gastosSap.clear(),
  ]);
  // Invalidamos el promise memoizado del seed para que NO se ejecute en
  // el próximo render — el reset total deja la base intencionalmente vacía.
  seedPromise = Promise.resolve();
  // Activamos modo operación persistente (sobrevive a reloads) para que
  // ningún `ensureSeed*` de módulos satélite (Riesgos, Stakeholders, etc.)
  // resuelva el "auto-seed" al navegar al módulo.
  if (activarOp) activarModoOperacion();
}

/**
 * Reset SELECTIVO: borra solo las tablas de las categorías indicadas
 * (ver `CATEGORIAS_RESET`). Las categorías no seleccionadas quedan intactas.
 *
 * Igual que `wipeAllTables`, neutraliza el seed memoizado y activa modo
 * operación: así lo borrado NO se vuelve a sembrar al recargar (un seed
 * parcial repoblaría las tablas vacías y desharía la limpieza). El admin
 * decidió que esta base ya es "real".
 */
export async function wipeCategorias(
  keys: string[],
  opts: { activarOperacion?: boolean } = {},
): Promise<void> {
  const activarOp = opts.activarOperacion ?? true;
  const seleccion = CATEGORIAS_RESET.filter((c) => keys.includes(c.key));
  const tablas = seleccion.flatMap((c) => c.tablas);
  await Promise.all(tablas.map((t) => t.clear()));
  // Mismo razonamiento que en `wipeAllTables`: evitamos el re-seed automático.
  seedPromise = Promise.resolve();
  if (activarOp) activarModoOperacion();
}

/**
 * Versión "reset total + seed" combinada: borra TODO (incluso configuración
 * que `resetSeedDemo` antes preservaba) y luego repuebla con los ejemplos.
 * Útil si los datos locales quedaron en un estado inconsistente y se quiere
 * un arranque "de fábrica" completo. Igual que `resetSeedDemo` vuelve a
 * modo demo.
 */
export async function resetSeedCompleto(): Promise<void> {
  desactivarModoOperacion();
  await wipeAllTables({ activarOperacion: false });
  seedPromise = doSeed();
  await seedPromise;
}

/**
 * Helper para construir tareas de SO con predecesoras resueltas por código.
 * Pasamos los codigos de predecesoras y el helper resuelve a IDs reales.
 */
/**
 * Catálogo de tarifas reales por id — espejo de las tarifas cargadas en
 * `sampleContratos`. Permite que cada tarea de seed se construya desde su
 * `tarifaId` sin duplicar codigo/unidad/precio en cada caller.
 */
const TARIFAS_INFO: Record<
  string,
  { codigo: string; unidad: string; precio: number }
> = {
  // Contrato 1 — Soforesta (CIVIL, USD)
  "tar-mt-001": { codigo: "1", unidad: "m3", precio: 25 },
  "tar-dr-001": { codigo: "2", unidad: "ml", precio: 120 },
  "tar-pv-001": { codigo: "3", unidad: "m2", precio: 45 },
  "tar-cn-001": { codigo: "4", unidad: "m3", precio: 180 },
  // Contrato 2 — Metálicas Brayan (MECANICA, USD)
  "tar-tu-001": { codigo: "1", unidad: "ml", precio: 85 },
  "tar-sd-001": { codigo: "2", unidad: "u", precio: 45 },
  "tar-pi-001": { codigo: "3", unidad: "u", precio: 350 },
  // Contrato 3 — Bobinados Electro Oriente (ELECTRICA, COP)
  "tar-tr-001": { codigo: "1", unidad: "km", precio: 3400 },
  "tar-tc-001": { codigo: "2", unidad: "km", precio: 6000 },
};

/**
 * Genera tareas de seed con el esquema vigente:
 *   - `tipo: "TAREA"` discriminador
 *   - `seq` numérico independiente (1, 2, 3...) — renumera al editar en UI
 *   - `codigo`, `unidad`, `precioUnitario` derivados de la tarifa real del CM
 *     vía `TARIFAS_INFO` (sin duplicar valores en cada caller)
 *   - `predecSeq` numérico al seq de la tarea predecesora (no IDs string)
 *   - `tipoRel: "FS"` y `lag: 0` por default
 *   - `predecesoras: []` (campo legacy mantenido para compat con datos viejos)
 */
function mkTareas(
  soId: string,
  defs: Array<{
    tarifaId: keyof typeof TARIFAS_INFO;
    desc: string;
    cant: number;
    ini: string;
    fin: string;
    /** seq de la tarea predecesora dentro de este mismo SO (1, 2, ...). */
    predSeq?: number;
    /** Avance ejecutado (0..100). Default 0. */
    avance?: number;
  }>,
): Tarea[] {
  const nowIso = new Date().toISOString();
  return defs.map((d, idx) => {
    const tarifa = TARIFAS_INFO[d.tarifaId];
    if (!tarifa) throw new Error(`Tarifa desconocida en seed: ${d.tarifaId}`);
    const seq = idx + 1;
    const ms =
      new Date(d.fin + "T00:00:00").getTime() -
      new Date(d.ini + "T00:00:00").getTime();
    const dur = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
    const av = d.avance ?? 0;
    return {
      id: `seed-tarea-${soId.slice(-3)}-${seq}`,
      tenantId: TENANT_LOCAL,
      soId,
      tipo: "TAREA",
      seq,
      orden: idx,
      codigo: tarifa.codigo,
      descripcion: d.desc,
      cantidad: d.cant,
      unidad: tarifa.unidad,
      tarifaId: d.tarifaId,
      precioUnitario: tarifa.precio,
      duracionDias: dur,
      fechaInicio: d.ini,
      fechaFin: d.fin,
      predecesoras: [],
      predecSeq: d.predSeq ?? null,
      tipoRel: "FS",
      lag: 0,
      cantidadEjecutada: Math.round((d.cant * av) / 100),
      porcentajeAvance: av,
      version: 1,
      createdAt: nowIso,
      updatedAt: nowIso,
      createdBy: "system",
      updatedBy: "system",
    };
  });
}

/**
 * Migración de datos: convierte SOs con estados obsoletos (EN_EJECUCION,
 * EXTENDIDO u otros previos a la simplificación de la máquina de estados) a
 * APROBADA, y limpia sub-flags de revisión que ya no aplican. Corre en cada
 * arranque — también sanea datos persistidos antes del refactor o recargados
 * desde un snapshot viejo.
 */
/**
 * Migración de tenant: durante un período corto del refactor de auth, el
 * `LocalAuthProvider` consultaba el tenant `"default"` mientras el seed
 * escribía con `"local-dev"`. Quien creó AFEs/PRs/POs/SOs/usuarios en esa
 * ventana terminó con filas etiquetadas `"default"` que el provider actual
 * (correcto, `"local-dev"`) ya no encontraba — la pantalla aparece vacía.
 *
 * Esta migración corre en cada arranque: detecta cualquier fila con tenantId
 * `"default"` en TODAS las tablas tenant-aware y la reescribe a `"local-dev"`.
 * Es idempotente: si no hay nada que migrar, retorna sin tocar la base.
 */
async function migrarTenantDefault(): Promise<void> {
  const TENANT_VIEJO = "default";
  // Tablas que tienen tenantId. Mantener sincronizada con el schema de db.ts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tablas: { nombre: string; t: any }[] = [
    { nombre: "budgets", t: localDb.budgets },
    { nombre: "costesCatalogo", t: localDb.costesCatalogo },
    { nombre: "afes", t: localDb.afes },
    { nombre: "contratosMarco", t: localDb.contratosMarco },
    { nombre: "prs", t: localDb.prs },
    { nombre: "pos", t: localDb.pos },
    { nombre: "mrs", t: localDb.mrs },
    { nombre: "sos", t: localDb.sos },
    { nombre: "rds", t: localDb.rds },
    { nombre: "eventos", t: localDb.eventos },
    { nombre: "actas", t: localDb.actas },
    { nombre: "usuarios", t: localDb.usuarios },
    { nombre: "riesgos", t: localDb.riesgos },
    { nombre: "stakeholders", t: localDb.stakeholders },
    { nombre: "listadosPendientes", t: localDb.listadosPendientes },
    { nombre: "pendientesComm", t: localDb.pendientesComm },
    { nombre: "sistemasComm", t: localDb.sistemasComm },
    { nombre: "subsistemasComm", t: localDb.subsistemasComm },
    { nombre: "configuracionesNotificacion", t: localDb.configuracionesNotificacion },
    { nombre: "listasDistribucion", t: localDb.listasDistribucion },
    { nombre: "notificacionesEnviadas", t: localDb.notificacionesEnviadas },
    { nombre: "auditLog", t: localDb.auditLog },
  ];
  let totalMigradas = 0;
  for (const { nombre, t } of tablas) {
    if (!t || typeof t.toCollection !== "function") continue;
    try {
      // Usamos `toCollection().filter(...).modify(...)` para no depender del
      // índice `tenantId` (varias tablas no lo tienen) — Dexie hace scan
      // completo, lo cual es aceptable porque solo corre en migraciones.
      const migradas = await t
        .toCollection()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((row: any) => row && row.tenantId === TENANT_VIEJO)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .modify((row: any) => {
          row.tenantId = TENANT_LOCAL;
        });
      if (migradas > 0) {
        totalMigradas += migradas;
        console.info(`[migración tenant] ${nombre}: ${migradas} filas movidas a "${TENANT_LOCAL}".`);
      }
    } catch (e) {
      console.warn(`[migración tenant] tabla ${nombre} falló:`, e);
    }
  }
  if (totalMigradas > 0) {
    console.info(`[migración tenant] Total: ${totalMigradas} filas recuperadas del tenant antiguo "${TENANT_VIEJO}".`);
  }
}

async function migrarSosObsoletas(): Promise<void> {
  const sos = await localDb.sos.toArray();
  const validos = new Set<string>(ESTADOS_SO);
  const obsoletas = sos.filter((s) => !validos.has(s.estado));
  if (obsoletas.length === 0) return;
  const ahora = new Date().toISOString();
  for (const so of obsoletas) {
    await localDb.sos
      .where("id")
      .equals(so.id)
      .modify((s) => {
        s.estado = "APROBADA";
        delete s.nivelRevision;
        s.enReprogramacion = false;
        s.updatedAt = ahora;
      });
  }
  console.info(
    `[migración] ${obsoletas.length} SO con estado obsoleto migradas a APROBADA.`,
  );
}

/**
 * Asigna código humano (`EV-{parentCodigo}-{NN}`) a eventos que aún no lo
 * tienen — los del seed antiguo y cualquier evento creado antes de
 * incorporar la generación de códigos en `DexieEventoRepo.create`.
 *
 * Se usa el helper `generarCodigoEvento` con SO/AFE padre, y se procesa el
 * universo ordenado por `createdAt` para que el consecutivo refleje el orden
 * histórico de los reportes.
 */
/**
 * Sintetiza un historial razonable de transiciones para PRs/POs/SOs que
 * vienen del seed sin `historial` cargado. El usuario debe poder abrir la
 * pestaña "Historial" y ver cómo llegó la entidad a su estado actual, en
 * lugar de un mensaje vacío "Sin historial".
 *
 * Estrategia: para cada entidad sin historial, calculamos la ruta canónica
 * en la máquina de estados que lleva desde el estado inicial hasta el
 * estado actual, y materializamos una entrada por cada salto. Las fechas
 * se distribuyen entre `createdAt` y `updatedAt`.
 *
 * Es idempotente: solo toca entidades con historial vacío.
 */
async function backfillHistorialEntidades(): Promise<void> {
  // ── Rutas canónicas de cada máquina de estados ────────────────────────
  // Cubre el "happy path" — para entidades en CANCELADA/RECHAZADA se inserta
  // una transición desde el penúltimo paso del happy path al estado final.
  const rutaPr: Record<string, string[]> = {
    CREADA_NO_APROBADA: ["BORRADOR", "CREADA_NO_APROBADA"],
    APROBADA: ["BORRADOR", "CREADA_NO_APROBADA", "APROBADA"],
    EE_ABAS: ["BORRADOR", "CREADA_NO_APROBADA", "APROBADA", "EE_ABAS"],
    ET_FAC: ["BORRADOR", "CREADA_NO_APROBADA", "APROBADA", "EE_ABAS", "ET_FAC"],
    ADJ_PARCIAL: [
      "BORRADOR",
      "CREADA_NO_APROBADA",
      "APROBADA",
      "EE_ABAS",
      "ET_FAC",
      "ADJ_PARCIAL",
    ],
    ADJ_TOTAL: [
      "BORRADOR",
      "CREADA_NO_APROBADA",
      "APROBADA",
      "EE_ABAS",
      "ET_FAC",
      "ADJ_PARCIAL",
      "ADJ_TOTAL",
    ],
    CANCELADA: ["BORRADOR", "CREADA_NO_APROBADA", "CANCELADA"],
  };
  const rutaPo: Record<string, string[]> = {
    EMITIDA: ["EMITIDA"],
    EN_ESPERA_ENTREGA: ["EMITIDA", "EN_ESPERA_ENTREGA"],
    ENTREGADA: ["EMITIDA", "EN_ESPERA_ENTREGA", "ENTREGADA"],
    PAGADA: ["EMITIDA", "EN_ESPERA_ENTREGA", "ENTREGADA", "PAGADA"],
    VENCIDA: ["EMITIDA", "EN_ESPERA_ENTREGA", "VENCIDA"],
    CANCELADA: ["EMITIDA", "CANCELADA"],
  };
  const rutaSo: Record<string, string[]> = {
    EN_COTIZACION: ["EN_COTIZACION"],
    EN_REVISION: ["EN_COTIZACION", "EN_REVISION"],
    APROBADA: ["EN_COTIZACION", "EN_REVISION", "APROBADA"],
    EN_EJECUCION: ["EN_COTIZACION", "EN_REVISION", "APROBADA", "EN_EJECUCION"],
    CERRADA: [
      "EN_COTIZACION",
      "EN_REVISION",
      "APROBADA",
      "EN_EJECUCION",
      "CERRADA",
    ],
    CANCELADA: ["EN_COTIZACION", "CANCELADA"],
  };

  /** Distribuye fechas ISO entre `desde` y `hasta` para N pasos. */
  const fechasIntermedias = (desde: string, hasta: string, n: number): string[] => {
    if (n <= 0) return [];
    const ini = new Date(desde).getTime();
    const fin = new Date(hasta).getTime();
    const paso = (fin - ini) / (n + 1);
    return Array.from({ length: n }, (_, i) => new Date(ini + paso * (i + 1)).toISOString());
  };

  const sintetizar = <T extends { fecha: string; estadoAnterior: string; estadoNuevo: string; usuario: string; observaciones?: string }>(
    ruta: string[],
    createdAt: string,
    updatedAt: string,
    usuario: string,
  ): T[] => {
    if (ruta.length < 2) return [];
    const saltos = ruta.length - 1;
    const fechas = fechasIntermedias(createdAt, updatedAt, saltos);
    return ruta.slice(1).map((estadoNuevo, i) => ({
      fecha: fechas[i] ?? updatedAt,
      estadoAnterior: ruta[i]!,
      estadoNuevo,
      usuario,
      observaciones: "Transición sintetizada para datos del seed.",
    })) as T[];
  };

  const [prs, pos, sos] = await Promise.all([
    localDb.prs.toArray(),
    localDb.pos.toArray(),
    localDb.sos.toArray(),
  ]);

  let nPrs = 0;
  for (const pr of prs) {
    if (pr.historial && pr.historial.length > 0) continue;
    const ruta = rutaPr[pr.estado];
    if (!ruta) continue;
    const usuario = pr.solicitadoPor ?? pr.createdBy ?? "system@seed";
    const historial = sintetizar<PrHistorialEntry>(ruta, pr.createdAt, pr.updatedAt, usuario);
    if (historial.length === 0) continue;
    await localDb.prs.update(pr.id, { historial });
    nPrs++;
  }

  let nPos = 0;
  for (const po of pos) {
    if (po.historial && po.historial.length > 0) continue;
    const ruta = rutaPo[po.estado];
    if (!ruta) continue;
    const usuario = po.createdBy ?? "system@seed";
    const historial = sintetizar<PoHistorialEntry>(ruta, po.createdAt, po.updatedAt, usuario);
    if (historial.length === 0) continue;
    await localDb.pos.update(po.id, { historial });
    nPos++;
  }

  let nSos = 0;
  for (const so of sos) {
    if (so.historial && so.historial.length > 0) continue;
    const ruta = rutaSo[so.estado];
    if (!ruta) continue;
    const usuario = so.createdBy ?? "system@seed";
    const historial = sintetizar<SoHistorialEntry>(ruta, so.createdAt, so.updatedAt, usuario);
    if (historial.length === 0) continue;
    await localDb.sos.update(so.id, { historial });
    nSos++;
  }

  if (nPrs + nPos + nSos > 0) {
    console.info(
      `[migración] Historial sintetizado: ${nPrs} PRs, ${nPos} POs, ${nSos} SOs.`,
    );
  }
}

async function backfillCodigosEventos(): Promise<void> {
  const { generarCodigoEvento } = await import("@features/eventos/eventosRules");
  const [eventos, sos, afes] = await Promise.all([
    localDb.eventos.toArray(),
    localDb.sos.toArray(),
    localDb.afes.toArray(),
  ]);
  const faltantes = eventos
    .filter((e) => !e.codigo || e.codigo === "")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (faltantes.length === 0) return;
  // Universo vivo de códigos ya asignados — se va engrosando a medida que
  // generamos nuevos para evitar colisiones dentro del propio backfill.
  const codigosVivos: string[] = eventos
    .map((e) => e.codigo)
    .filter((c): c is string => !!c);
  const ahora = new Date().toISOString();
  for (const ev of faltantes) {
    const soCodigo = ev.soId ? sos.find((s) => s.id === ev.soId)?.codigo : undefined;
    const afeCodigo = ev.afeId ? afes.find((a) => a.id === ev.afeId)?.codigo : undefined;
    const codigo = generarCodigoEvento({
      ...(soCodigo ? { soCodigo } : {}),
      ...(afeCodigo ? { afeCodigo } : {}),
      codigosExistentes: codigosVivos,
    });
    codigosVivos.push(codigo);
    await localDb.eventos.update(ev.id, { codigo, updatedAt: ahora });
  }
  console.info(`[migración] Códigos asignados a ${faltantes.length} eventos.`);
}

async function doSeed(): Promise<void> {
  // Saneamiento de datos previos al refactor — corre siempre, incluso si la
  // base ya tiene datos y el seed no es necesario.
  // Primero recuperamos cualquier dato huérfano del tenant viejo "default"
  // (ver migrarTenantDefault). Luego saneamos SOs con estados obsoletos.
  await migrarTenantDefault();
  await migrarSosObsoletas();

  // IMPORTANTE: contamos por tenant. Si la base tiene residuos de una corrida
  // previa con otro tenantId (p.ej. "default" antes del refactor), las tablas
  // no estarán vacías globalmente pero sí lo estarán para `TENANT_LOCAL`. El
  // seed debe correr en ese caso para repoblar al tenant actual.
  //
  // NO usamos `.where("tenantId")` porque varias tablas (p.ej. `tareas`) no
  // tienen ese índice y Dexie lanza `SchemaError` que rompía todo el seed
  // por Promise.all. Filtramos en JS — el costo es despreciable al arranque.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const porTenant = async (table: any): Promise<number> => {
     
    const rows = (await table.toArray()) as { tenantId?: string }[];
    return rows.filter((r) => r.tenantId === TENANT_LOCAL).length;
  };
  const [
    budgetCount,
    costeCount,
    afeCount,
    soCount,
    tareaCount,
    usuarioCount,
    eventoCount,
    rdCount,
    actaCount,
    contratoCount,
    prCount,
    poCount,
    mrCount,
  ] = await Promise.all([
    porTenant(localDb.budgets),
    porTenant(localDb.costesCatalogo),
    porTenant(localDb.afes),
    porTenant(localDb.sos),
    porTenant(localDb.tareas),
    porTenant(localDb.usuarios),
    porTenant(localDb.eventos),
    porTenant(localDb.rds),
    porTenant(localDb.actas),
    porTenant(localDb.contratosMarco),
    porTenant(localDb.prs),
    porTenant(localDb.pos),
    porTenant(localDb.mrs),
  ]);

  // Antes había un early-exit basado en si "todos los counts > 0", pero deja
  // al PMIS sin forma de auto-restaurar el listado de personal canónico si el
  // ADMIN borra usuarios (caso reportado en el piloto). Cada bloque de seed
  // ya está gateado por su propio `if (xCount === 0)` y la pasada extra cuesta
  // unos ms al arranque. El bloque de personal al final garantiza que los
  // usuarios `seed-user-*` siempre estén presentes.

  console.info(
    `[Dexie] Seeding... actuales: Budgets=${budgetCount}, Costes=${costeCount}, AFEs=${afeCount}, SOs=${soCount}, Tareas=${tareaCount}, Usuarios=${usuarioCount}, Eventos=${eventoCount}, RDs=${rdCount}, Actas=${actaCount}, Contratos=${contratoCount}, PRs=${prCount}, POs=${poCount}`,
  );

  const now = new Date().toISOString();

  // ===== Budgets (4 anos fiscales: 23-24 cerrado, 24-25 cerrado, 25-26 activo, 26-27 futuro) =====
  // Tecpetrol: ano fiscal 1-jul a 30-jun. Hoy ~ may-2026 -> 25-26 ACTIVO.
  const sampleBudgets: Budget[] = [
    // ─── Colombia (Bloque CPO-13, FAC) ──────────────────────────────
    {
      id: SEED_IDS.budget23_24,
      tenantId: TENANT_LOCAL,
      codigo: "23-24",
      gerenciaId: "CO",
      fechaInicio: "2023-07-01",
      fechaFin: "2024-06-30",
      estado: "CERRADO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.budget24_25,
      tenantId: TENANT_LOCAL,
      codigo: "24-25",
      gerenciaId: "CO",
      fechaInicio: "2024-07-01",
      fechaFin: "2025-06-30",
      estado: "CERRADO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.budget25_26,
      tenantId: TENANT_LOCAL,
      codigo: "25-26",
      gerenciaId: "CO",
      fechaInicio: "2025-07-01",
      fechaFin: "2026-06-30",
      estado: "ACTIVO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.budget26_27,
      tenantId: TENANT_LOCAL,
      codigo: "26-27",
      gerenciaId: "CO",
      fechaInicio: "2026-07-01",
      fechaFin: "2027-06-30",
      estado: "FUTURO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    // ─── Argentina (demo multi-gerencia F2 — Bloque Neuquén) ───────────
    // Budgets independientes para validar el switcher. Sin AFEs/PRs/POs
    // poblados todavía — el usuario los crea desde el PMIS al cambiar al
    // gerencia y ver la lista vacía. Sirve para demo: el switcher cambia y
    // se ven los Budgets argentinos en /budget.
    {
      id: "seed-budget-ar-25-26",
      tenantId: TENANT_LOCAL,
      codigo: "AR-25-26",
      gerenciaId: "AR",
      fechaInicio: "2025-07-01",
      fechaFin: "2026-06-30",
      estado: "ACTIVO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: "seed-budget-ar-26-27",
      tenantId: TENANT_LOCAL,
      codigo: "AR-26-27",
      gerenciaId: "AR",
      fechaInicio: "2026-07-01",
      fechaFin: "2027-06-30",
      estado: "FUTURO",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
  ];
  if (budgetCount === 0) await localDb.budgets.bulkAdd(sampleBudgets);

  // ===== Catalogo de Costes (12 codigos VD §7.12) =====
  const sampleCostes: CosteCatalogo[] = CLASES_COSTE.map((c) => ({
    id: `seed-coste-${c.codigo}`,
    tenantId: TENANT_LOCAL,
    // Fase 3 del ADR — catálogo de costes per-gerencia (Decisión 2).
    // El seed asigna la gerencia natural (CO) a todos los costes.
    gerenciaId: "CO" as const,
    codigo: c.codigo,
    nombre: c.nombre,
    activo: true,
    version: 1,
    createdAt: now,
    updatedAt: now,
    createdBy: "system",
    updatedBy: "system",
  }));
  if (costeCount === 0) await localDb.costesCatalogo.bulkAdd(sampleCostes);

  // ===== AFEs (modelo VD: Budget + TRM + costes con codigo + matriz mensual) =====
  // AFE 1: Plataforma CT-094 (DEFINITIVO, APROBADO, en Budget 25-26)
  const afe1Costos: AfeCosto[] = [
    { id: "c1-civ", costeCodigo: "A111004", moneda: "USD", valorDigitado: 600_000, valorUsd: 600_000, valorCop: 2_520_000_000 },
    { id: "c1-mec", costeCodigo: "A111006", moneda: "USD", valorDigitado: 380_000, valorUsd: 380_000, valorCop: 1_596_000_000 },
    { id: "c1-mat", costeCodigo: "A210005", moneda: "USD", valorDigitado: 270_000, valorUsd: 270_000, valorCop: 1_134_000_000 },
  ];
  const afe1Meses = ["2026-02", "2026-03", "2026-04", "2026-05", "2026-06"];
  const afe1Matriz: AfeCosteMensual[] = [];
  let mIdx = 0;
  for (const c of afe1Costos) {
    const porMes = Math.floor(c.valorCop / afe1Meses.length);
    const ultimo = c.valorCop - porMes * (afe1Meses.length - 1);
    afe1Meses.forEach((mes, i) => {
      const cop = i === afe1Meses.length - 1 ? ultimo : porMes;
      afe1Matriz.push({
        id: `m1-${mIdx++}`,
        costeCodigo: c.costeCodigo,
        mes,
        valorPlaneadoCop: cop,
        valorPlaneadoUsd: Math.round((cop / 4200) * 100) / 100,
      });
    });
  }

  // AFE 2: Vias Aguada (PROVISIONAL, EN_REVISION_JF, Budget 25-26)
  const afe2Costos: AfeCosto[] = [
    { id: "c2-civ", costeCodigo: "A111004", moneda: "USD", valorDigitado: 700_000, valorUsd: 700_000, valorCop: 2_905_000_000 },
    { id: "c2-top", costeCodigo: "80010005", moneda: "USD", valorDigitado: 80_000, valorUsd: 80_000, valorCop: 332_000_000 },
    { id: "c2-amb", costeCodigo: "71100001", moneda: "USD", valorDigitado: 70_000, valorUsd: 70_000, valorCop: 290_500_000 },
  ];

  // AFE 3: Mantenimiento C-101 (PROVISIONAL, BORRADOR, Budget 25-26)
  const afe3Costos: AfeCosto[] = [
    { id: "c3-mec", costeCodigo: "A111006", moneda: "USD", valorDigitado: 200_000, valorUsd: 200_000, valorCop: 836_000_000 },
    { id: "c3-mat", costeCodigo: "A210002", moneda: "USD", valorDigitado: 120_000, valorUsd: 120_000, valorCop: 501_600_000 },
  ];

  // AFE 4: Equipos rotativos planta deshidratación (DEFINITIVO, APROBADO)
  const afe4Costos: AfeCosto[] = [
    { id: "c4-mec", costeCodigo: "A111006", moneda: "USD", valorDigitado: 850_000, valorUsd: 850_000, valorCop: 3_570_000_000 },
    { id: "c4-mat", costeCodigo: "A210002", moneda: "USD", valorDigitado: 720_000, valorUsd: 720_000, valorCop: 3_024_000_000 },
    { id: "c4-ele", costeCodigo: "A111005", moneda: "USD", valorDigitado: 230_000, valorUsd: 230_000, valorCop: 966_000_000 },
  ];

  // AFE 5: Construcción tanques almacenamiento crudo (DEFINITIVO, APROBADO)
  const afe5Costos: AfeCosto[] = [
    { id: "c5-civ", costeCodigo: "A111004", moneda: "USD", valorDigitado: 1_200_000, valorUsd: 1_200_000, valorCop: 5_040_000_000 },
    { id: "c5-mat", costeCodigo: "A210005", moneda: "USD", valorDigitado: 850_000, valorUsd: 850_000, valorCop: 3_570_000_000 },
    { id: "c5-pip", costeCodigo: "A210002", moneda: "USD", valorDigitado: 350_000, valorUsd: 350_000, valorCop: 1_470_000_000 },
  ];

  // AFE 6: Modernización SCADA y telemetría planta (DEFINITIVO, APROBADO)
  const afe6Costos: AfeCosto[] = [
    { id: "c6-ins", costeCodigo: "A210003", moneda: "USD", valorDigitado: 580_000, valorUsd: 580_000, valorCop: 2_436_000_000 },
    { id: "c6-ing", costeCodigo: "A111001", moneda: "USD", valorDigitado: 220_000, valorUsd: 220_000, valorCop: 924_000_000 },
    { id: "c6-mon", costeCodigo: "A111005", moneda: "USD", valorDigitado: 150_000, valorUsd: 150_000, valorCop: 630_000_000 },
  ];

  // AFE 7: Interventoría externa obras 2026 (DEFINITIVO, APROBADO)
  const afe7Costos: AfeCosto[] = [
    { id: "c7-int", costeCodigo: "71100003", moneda: "USD", valorDigitado: 280_000, valorUsd: 280_000, valorCop: 1_176_000_000 },
    { id: "c7-amb", costeCodigo: "71100001", moneda: "USD", valorDigitado: 100_000, valorUsd: 100_000, valorCop: 420_000_000 },
  ];

  // AFE 8: Servicios transporte y logística 2026 (DEFINITIVO, APROBADO)
  const afe8Costos: AfeCosto[] = [
    { id: "c8-mov", costeCodigo: "80040003", moneda: "USD", valorDigitado: 700_000, valorUsd: 700_000, valorCop: 2_940_000_000 },
    { id: "c8-sup", costeCodigo: "71100003", moneda: "USD", valorDigitado: 380_000, valorUsd: 380_000, valorCop: 1_596_000_000 },
    { id: "c8-top", costeCodigo: "80010005", moneda: "USD", valorDigitado: 220_000, valorUsd: 220_000, valorCop: 924_000_000 },
  ];

  const sampleAfes: Afe[] = [
    {
      id: SEED_IDS.afe1,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00094",
      nombre: "Construccion Plataforma Pozo CT-094",
      descripcion: "Adecuacion de plataforma para campana de perforacion en Casanare",
      budgetId: "25-26",
      fechaInicio: "2026-02-01",
      fechaFin: "2026-06-30",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "daniel.medina@tecpetrol.com",
      backPlId: "franklin.torres@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 1_250_000,
      valorAprobadoCop: 5_250_000_000,
      estado: "APROBADO",
      costos: afe1Costos,
      distribucionMensual: afe1Matriz,
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "daniel.medina@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe2,
      tenantId: TENANT_LOCAL,
      tipo: "PROVISIONAL",
      codigo: "PROV-0042",
      nombre: "Vias de acceso campo Aguada",
      descripcion: "Mejoramiento de vias de acceso al campo Aguada Pichana",
      budgetId: "25-26",
      fechaInicio: "2026-03-01",
      fechaFin: "2026-06-30",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "franklin.torres@tecpetrol.com",
      backPlId: "daniel.medina@tecpetrol.com",
      trm: 4150,
      valorAprobadoUsd: 850_000,
      valorAprobadoCop: 3_527_500_000,
      estado: "EN_REVISION_JF",
      costos: afe2Costos,
      distribucionMensual: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe3,
      tenantId: TENANT_LOCAL,
      tipo: "PROVISIONAL",
      codigo: "PROV-0043",
      nombre: "Mantenimiento mayor compresor C-101",
      budgetId: "25-26",
      fechaInicio: "2026-05-01",
      fechaFin: "2026-06-30",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "eduardo.zambrano@tecpetrol.com",
      backPlId: "rafael.garcia@tecpetrol.com",
      trm: 4180,
      valorAprobadoUsd: 320_000,
      valorAprobadoCop: 1_337_600_000,
      estado: "BORRADOR",
      costos: afe3Costos,
      distribucionMensual: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "eduardo.zambrano@tecpetrol.com",
      updatedBy: "eduardo.zambrano@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe4,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00102",
      nombre: "Compra equipos rotativos planta deshidratación",
      descripcion: "Suministro de bombas, compresores y equipos rotativos para planta de deshidratación CT-094",
      budgetId: "25-26",
      fechaInicio: "2026-01-15",
      fechaFin: "2026-08-31",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "rafael.garcia@tecpetrol.com",
      backPlId: "eduardo.zambrano@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 1_800_000,
      valorAprobadoCop: 7_560_000_000,
      estado: "APROBADO",
      costos: afe4Costos,
      distribucionMensual: [],
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe5,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00105",
      nombre: "Construcción tanques almacenamiento crudo Aguada",
      descripcion: "Construcción de tres tanques de almacenamiento de crudo de 5000 bbl c/u en campo Aguada",
      budgetId: "25-26",
      fechaInicio: "2026-01-10",
      fechaFin: "2026-09-30",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "franklin.torres@tecpetrol.com",
      backPlId: "daniel.medina@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 2_400_000,
      valorAprobadoCop: 10_080_000_000,
      estado: "APROBADO",
      costos: afe5Costos,
      distribucionMensual: [],
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe6,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00108",
      nombre: "Modernización SCADA y telemetría planta",
      descripcion: "Actualización del sistema SCADA, RTUs y telemetría de la planta principal",
      budgetId: "25-26",
      fechaInicio: "2026-02-01",
      fechaFin: "2026-08-15",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "rafael.garcia@tecpetrol.com",
      backPlId: "eduardo.zambrano@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 950_000,
      valorAprobadoCop: 3_990_000_000,
      estado: "APROBADO",
      costos: afe6Costos,
      distribucionMensual: [],
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe7,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00110",
      nombre: "Interventoría externa obras 2026",
      descripcion: "Servicios de interventoría externa para todas las obras del programa 2026",
      budgetId: "25-26",
      fechaInicio: "2026-01-15",
      fechaFin: "2026-12-31",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "franklin.torres@tecpetrol.com",
      backPlId: "daniel.medina@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 380_000,
      valorAprobadoCop: 1_596_000_000,
      estado: "APROBADO",
      costos: afe7Costos,
      distribucionMensual: [],
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    {
      id: SEED_IDS.afe8,
      tenantId: TENANT_LOCAL,
      tipo: "DEFINITIVO",
      codigo: "CTFA-00112",
      nombre: "Servicios transporte y logística 2026",
      descripcion: "Contratos marco para transporte de personal, carga y movimiento de tierras del programa 2026",
      budgetId: "25-26",
      fechaInicio: "2026-01-01",
      fechaFin: "2026-12-31",
      pmId: "nestor.contreras@tecpetrol.com",
      plId: "daniel.medina@tecpetrol.com",
      backPlId: "franklin.torres@tecpetrol.com",
      trm: 4200,
      valorAprobadoUsd: 1_300_000,
      valorAprobadoCop: 5_460_000_000,
      estado: "APROBADO",
      costos: afe8Costos,
      distribucionMensual: [],
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      fechaAprobacion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "daniel.medina@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
  ];
  if (afeCount === 0) {
    // Prioriza el snapshot guardado por el ADMIN_PMIS (PL/back-PL ajustados
    // para pruebas). Si no hay snapshot, cae al baseline de código.
    const snapshotAfes = leerAfesDefault();
    await localDb.afes.bulkAdd(snapshotAfes ?? sampleAfes);
  }

  // ===== Órdenes de Trabajo (Capa 3 las refactoriza con costeCodigo) =====
  const so1Id = SEED_IDS.so1;
  const so2Id = SEED_IDS.so2;
  const so3Id = SEED_IDS.so3;

  const sampleSos: So[] = [
    // OT-1: APROBADA desde Contrato Marco — Adecuación plataforma CT-094
    {
      id: so1Id,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-001",
      nombre: "Adecuación plataforma CT-094",
      alcance: "Movimiento de tierras, drenajes y pavimento de la plataforma del pozo CT-094",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "fredyvanegas@ingecontrol.com.co",
      monto: 250_000,
      moneda: "USD",
      fechaInicio: "2026-02-01",
      fechaFin: "2026-05-15",
      fechaSolicitudCotizacion: "2026-01-15",
      fechaRecepcionCotizacion: "2026-01-22",
      fechaAprobacion: "2026-01-28",
      fechaInicioEjecucion: "2026-02-03",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-2: APROBADA desde Contrato Marco — Líneas de producción
    {
      id: so2Id,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-002",
      nombre: "Instalación líneas de producción CT-094",
      alcance: "Tendido de tubería 4 pulgadas y conexiones desde plataforma a manifold",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 180_000,
      moneda: "USD",
      fechaInicio: "2026-05-01",
      fechaFin: "2026-07-30",
      fechaSolicitudCotizacion: "2026-04-05",
      fechaRecepcionCotizacion: "2026-04-15",
      fechaAprobacion: "2026-04-25",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    // OT-3: COTIZACION_RECIBIDA — Vías Aguada
    {
      id: so3Id,
      tenantId: TENANT_LOCAL,
      codigo: "OT-VIA-001",
      nombre: "Mejoramiento vía principal Aguada",
      alcance: "Recapeo, drenajes laterales y señalización vial primaria del campo",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe2,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "interventorcivil@ingecontrol.com.co",
      monto: 320_000,
      moneda: "USD",
      fechaInicio: "2026-05-15",
      fechaFin: "2026-09-30",
      fechaSolicitudCotizacion: "2026-04-20",
      fechaRecepcionCotizacion: "2026-05-02",
      estado: "EN_REVISION",
      nivelRevision: "PL2",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-4: CERRADA — Adecuación drenajes Aguada (cumplida en plazo, ejemplo de éxito)
    // (Antes "Topografía" asignada por error al CM eléctrico de Bobinados — la
    //  topografía no aplica a ese contrato. Migrada a obra civil de Soforesta.)
    {
      id: SEED_IDS.so4,
      tenantId: TENANT_LOCAL,
      codigo: "OT-VIA-002",
      nombre: "Adecuación drenajes vías Aguada",
      alcance: "Construcción de cunetas en concreto y limpieza de drenajes existentes",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe2,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "interventorcivil@ingecontrol.com.co",
      monto: 80_000,
      moneda: "USD",
      fechaInicio: "2026-02-15",
      fechaFin: "2026-04-15",
      fechaSolicitudCotizacion: "2026-01-30",
      fechaRecepcionCotizacion: "2026-02-08",
      fechaAprobacion: "2026-02-12",
      fechaInicioEjecucion: "2026-02-15",
      fechaCierre: "2026-04-10",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "CERRADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-5: ADICIONAL sobre OT-1 — alcance extra que surgió en obra
    {
      id: SEED_IDS.so5,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-001-A",
      nombre: "Adicional CT-094: muro de contención lado norte",
      alcance: "Construcción de muro de contención en gaviones lado norte (hallazgo en obra)",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      parentSoId: SEED_IDS.so1,
      esAdicional: true,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "fredyvanegas@ingecontrol.com.co",
      monto: 45_000,
      moneda: "USD",
      fechaInicio: "2026-04-15",
      fechaFin: "2026-05-30",
      fechaSolicitudCotizacion: "2026-04-05",
      fechaRecepcionCotizacion: "2026-04-10",
      fechaAprobacion: "2026-04-14",
      fechaInicioEjecucion: "2026-04-16",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      notas: "Hallazgo en obra durante movimiento de tierras — talud inestable que requiere refuerzo.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-6: SOLICITADA — recién creada, contratista por iniciar cotización
    {
      id: SEED_IDS.so6,
      tenantId: TENANT_LOCAL,
      codigo: "OT-AGD-002",
      nombre: "Drenajes pluviales sector norte Aguada",
      alcance: "Construcción de cunetas y drenajes longitudinales en 1.2 km de vía secundaria",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "interventorcivil@ingecontrol.com.co",
      monto: 144_000,
      moneda: "USD",
      fechaInicio: "2026-06-01",
      fechaFin: "2026-08-15",
      fechaSolicitudCotizacion: "2026-05-08",
      estado: "EN_COTIZACION",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-7: EN_COTIZACION — contratista trabajando en el PDT
    {
      id: SEED_IDS.so7,
      tenantId: TENANT_LOCAL,
      codigo: "OT-TQ-001",
      nombre: "Excavación y conformación tanques crudo Aguada",
      alcance: "Movimiento de tierras y conformación de plataforma para 3 tanques de 5000 bbl",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "fredyvanegas@ingecontrol.com.co",
      monto: 280_000,
      moneda: "USD",
      fechaInicio: "2026-06-15",
      fechaFin: "2026-09-30",
      fechaSolicitudCotizacion: "2026-05-02",
      estado: "EN_COTIZACION",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-8: EN_REVISION_PL2 — cotización enviada por el contratista
    {
      id: SEED_IDS.so8,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-003",
      nombre: "Ampliación líneas de producción CT-094 — fase 2",
      alcance: "Tendido de 2 km de tubería 4 pulg + soldaduras + pruebas hidrostáticas",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 215_000,
      moneda: "USD",
      fechaInicio: "2026-06-01",
      fechaFin: "2026-08-31",
      fechaSolicitudCotizacion: "2026-05-01",
      fechaRecepcionCotizacion: "2026-05-09",
      estado: "EN_REVISION",
      nivelRevision: "PL2",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    // OT-9: RECHAZADA_VOLVER_CONTRATISTA — PL2 rechazó con observaciones
    {
      id: SEED_IDS.so9,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-004",
      nombre: "Mantenimiento preventivo bombas CT-094",
      alcance: "Servicio de mantenimiento preventivo de 6 bombas centrífugas + cambio de sellos",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "dannyleon@ingecontrol.com.co",
      monto: 95_000,
      moneda: "USD",
      fechaInicio: "2026-06-10",
      fechaFin: "2026-07-20",
      fechaSolicitudCotizacion: "2026-04-25",
      fechaRecepcionCotizacion: "2026-05-04",
      estado: "EN_COTIZACION",
      // SO devuelta por el PL2; el contratista debe ajustar antes de reenviar.
      notas: "Devuelta por el PL2: tarifas no corresponden al contrato vigente; revisar PDT y cantidades de soldaduras.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    // OT-10: EN_REVISION_PL — PL2 propuso al PL
    {
      id: SEED_IDS.so10,
      tenantId: TENANT_LOCAL,
      codigo: "OT-TQ-002",
      nombre: "Construcción cimentación tanques crudo Aguada",
      alcance: "Excavación, armado y vaciado de losas de cimentación para 3 tanques",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "fredyvanegas@ingecontrol.com.co",
      monto: 380_000,
      moneda: "USD",
      fechaInicio: "2026-07-01",
      fechaFin: "2026-10-30",
      fechaSolicitudCotizacion: "2026-04-10",
      fechaRecepcionCotizacion: "2026-04-22",
      estado: "EN_REVISION",
      nivelRevision: "PL",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-11: EN_REVISION (nivelRevision=PL) — OT de alto monto en revisión del PL
    {
      id: SEED_IDS.so11,
      tenantId: TENANT_LOCAL,
      codigo: "OT-TQ-003",
      nombre: "Suministro y montaje placas tanques almacenamiento",
      alcance: "Fabricación, suministro y montaje de placas de acero para 3 tanques de 5000 bbl",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "interventorcivil@ingecontrol.com.co",
      monto: 720_000,
      moneda: "USD",
      fechaInicio: "2026-08-01",
      fechaFin: "2026-12-15",
      fechaSolicitudCotizacion: "2026-04-15",
      fechaRecepcionCotizacion: "2026-04-28",
      estado: "EN_REVISION",
      nivelRevision: "PL",
      notas: "OT de alto monto bajo revisión del PL.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-12: APROBADA_LB — línea base congelada, lista para iniciar
    {
      id: SEED_IDS.so12,
      tenantId: TENANT_LOCAL,
      codigo: "OT-EQR-001",
      nombre: "Montaje skid de bombeo planta deshidratación",
      alcance: "Recepción, montaje, conexión y prueba de skid de 4 bombas API 610",
      ubicacion: "Planta Deshidratación CT-094, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 165_000,
      moneda: "USD",
      fechaInicio: "2026-06-01",
      fechaFin: "2026-08-15",
      fechaSolicitudCotizacion: "2026-04-05",
      fechaRecepcionCotizacion: "2026-04-15",
      fechaAprobacion: "2026-05-01",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },
    // OT-13: EN_REPROGRAMACION — solicitud de cambio de LB en obra activa
    {
      id: SEED_IDS.so13,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-005",
      nombre: "Pavimentación adicional vía acceso CT-094",
      alcance: "Pavimentación de 800 m2 adicionales por extensión de plataforma",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "fredyvanegas@ingecontrol.com.co",
      monto: 95_000,
      moneda: "USD",
      fechaInicio: "2026-03-15",
      fechaFin: "2026-06-30",
      fechaSolicitudCotizacion: "2026-02-10",
      fechaRecepcionCotizacion: "2026-02-20",
      fechaAprobacion: "2026-03-01",
      fechaInicioEjecucion: "2026-03-18",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      notas: "Reprogramación solicitada por hallazgo de roca en subbase. PL2 evaluando nueva LB.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-14: EN_CIERRE — avance al 100%, esperando cierre formal del PL
    {
      id: SEED_IDS.so14,
      tenantId: TENANT_LOCAL,
      codigo: "OT-AGD-001",
      nombre: "Adecuación caseta de operadores Aguada",
      alcance: "Construcción de caseta 6x4m incluyendo cimentación, mampostería, cubierta",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A111004",
      contratoMarcoId: SEED_IDS.contrato1,
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      interventorId: "interventorcivil@ingecontrol.com.co",
      monto: 68_000,
      moneda: "USD",
      fechaInicio: "2026-01-15",
      fechaFin: "2026-04-30",
      fechaSolicitudCotizacion: "2025-12-10",
      fechaRecepcionCotizacion: "2025-12-22",
      fechaAprobacion: "2026-01-08",
      fechaInicioEjecucion: "2026-01-16",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "APROBADA",
      notas: "Obra finalizada al 100%. Falta entregar dossier de construcción y última acta firmada.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // OT-15: CERRADA — histórica completa con dossier entregado
    {
      id: SEED_IDS.so15,
      tenantId: TENANT_LOCAL,
      codigo: "OT-094-006",
      nombre: "Conexionado eléctrico tableros CT-094",
      alcance: "Conexionado en obra de 4 tableros de 480V incluido cableado, marquillas y pruebas",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventorelectrico@ingecontrol.com.co",
      monto: 88_000,
      moneda: "USD",
      fechaInicio: "2026-01-10",
      fechaFin: "2026-03-28",
      fechaSolicitudCotizacion: "2025-12-05",
      fechaRecepcionCotizacion: "2025-12-15",
      fechaAprobacion: "2025-12-20",
      fechaInicioEjecucion: "2026-01-10",
      fechaCierre: "2026-04-02",
      aprobadoPor: "nestor.contreras@tecpetrol.com",
      estado: "CERRADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },

    // ========================================================================
    // SOs 16-30 — Metálicas Brayan (contrato2), cobertura completa de estados
    // ========================================================================
    // 15 SOs con scopes coherentes con las tarifas del contrato2 (tendido
    // tubería 4", soldadura, prueba hidrostática). Distribución por estado:
    //   EN_COTIZACION × 2  (sin tareas / con tareas parciales)
    //   EN_REVISION   × 4  (2 PL2 + 2 PL)
    //   APROBADA      × 6  (línea base congelada, habilitadas para RD)
    //   CERRADA       × 1
    //   CANCELADA     × 2

    // OT-16: EN_COTIZACION — recién creada por el PL, contratista pendiente
    {
      id: SEED_IDS.so16,
      tenantId: TENANT_LOCAL,
      codigo: "OT-201",
      nombre: "Tendido líneas de surtido FCS Aguada Norte",
      alcance:
        "Tendido de tubería de 4 pulg para nuevas líneas de flujo desde pad Aguada Norte hasta el manifold central. Incluye soldaduras y pruebas hidrostáticas.",
      ubicacion: "Pad Aguada Norte, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 0,
      moneda: "USD",
      fechaInicio: "2026-06-01",
      fechaFin: "2026-08-30",
      fechaSolicitudCotizacion: "2026-05-10",
      estado: "EN_COTIZACION",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "samir.perez@tecpetrol.com",
    },

    // OT-17: EN_COTIZACION — contratista ya empezó a cargar tareas
    {
      id: SEED_IDS.so17,
      tenantId: TENANT_LOCAL,
      codigo: "OT-202",
      nombre: "Cambio de niples manifold CT-094",
      alcance:
        "Reemplazo de 12 niples de proceso del manifold de inyección de agua, incluyendo soldaduras certificadas y prueba hidrostática del conjunto.",
      ubicacion: "Pozo CT-094, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 5_390,
      moneda: "USD",
      fechaInicio: "2026-06-15",
      fechaFin: "2026-07-10",
      fechaSolicitudCotizacion: "2026-05-15",
      estado: "EN_COTIZACION",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-18: EN_REVISION (PL2) — cotización entregada, pendiente PL2
    {
      id: SEED_IDS.so18,
      tenantId: TENANT_LOCAL,
      codigo: "OT-203",
      nombre: "Pruebas hidrostáticas anuales líneas de producción 2026",
      alcance:
        "Ejecución de 30 pruebas hidrostáticas a líneas de producción de campo Aguada, según cronograma anual de integridad mecánica.",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 10_500,
      moneda: "USD",
      fechaInicio: "2026-07-01",
      fechaFin: "2026-08-15",
      fechaSolicitudCotizacion: "2026-05-12",
      fechaRecepcionCotizacion: "2026-05-22",
      estado: "EN_REVISION",
      nivelRevision: "PL2",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-19: EN_REVISION (PL2) — otra cotización en revisión PL2
    {
      id: SEED_IDS.so19,
      tenantId: TENANT_LOCAL,
      codigo: "OT-204",
      nombre: "Soldadura conexiones FCS-008 a header",
      alcance:
        "Soldadura certificada de 120 uniones de tubería para conectar nueva FCS-008 al header existente del campo Aguada.",
      ubicacion: "Campo Aguada, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 10_950,
      moneda: "USD",
      fechaInicio: "2026-07-15",
      fechaFin: "2026-08-30",
      fechaSolicitudCotizacion: "2026-05-14",
      fechaRecepcionCotizacion: "2026-05-25",
      estado: "EN_REVISION",
      nivelRevision: "PL2",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-20: EN_REVISION (PL) — PL2 ya validó técnicamente, espera firma PL
    {
      id: SEED_IDS.so20,
      tenantId: TENANT_LOCAL,
      codigo: "OT-205",
      nombre: "Cambio de header inyección de agua sector sur",
      alcance:
        "Suministro, soldadura y prueba hidrostática del header de 4 pulg del sistema de inyección de agua sector sur, ~430 ml de tubería y 50 uniones soldadas.",
      ubicacion: "Sector Sur, Campo Aguada",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 49_050,
      moneda: "USD",
      fechaInicio: "2026-07-15",
      fechaFin: "2026-10-15",
      fechaSolicitudCotizacion: "2026-04-30",
      fechaRecepcionCotizacion: "2026-05-12",
      estado: "EN_REVISION",
      nivelRevision: "PL",
      notas: "PL2 validó alcance técnico. PL revisa antes de firmar APROBADA.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-21: EN_REVISION (PL) — alto monto, PL2 lo escaló al PL
    {
      id: SEED_IDS.so21,
      tenantId: TENANT_LOCAL,
      codigo: "OT-206",
      nombre: "Recablado líneas de flujo CT-094 cluster norte",
      alcance:
        "Cambio completo de líneas de flujo del cluster norte: 1200 ml de tubería de 4 pulg, 180 uniones soldadas y 60 pruebas hidrostáticas en aislamientos.",
      ubicacion: "Cluster Norte CT-094, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 130_200,
      moneda: "USD",
      fechaInicio: "2026-08-01",
      fechaFin: "2026-12-15",
      fechaSolicitudCotizacion: "2026-04-15",
      fechaRecepcionCotizacion: "2026-05-08",
      estado: "EN_REVISION",
      nivelRevision: "PL",
      notas: "Escalado al PL por monto > 5% del coste asignado.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-22: APROBADA — línea base congelada, lista para iniciar
    {
      id: SEED_IDS.so22,
      tenantId: TENANT_LOCAL,
      codigo: "OT-207",
      nombre: "Tendido tubería FCS Pad 5 → Manifold",
      alcance:
        "Tendido de 350 ml de tubería de 4 pulg desde FCS Pad 5 al manifold central, incluyendo 40 uniones soldadas y pruebas hidrostáticas correspondientes.",
      ubicacion: "Pad 5, Campo Aguada",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 31_550,
      moneda: "USD",
      fechaInicio: "2026-06-15",
      fechaFin: "2026-09-30",
      fechaSolicitudCotizacion: "2026-04-20",
      fechaRecepcionCotizacion: "2026-04-30",
      fechaAprobacion: "2026-05-15",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-23: APROBADA — otra obra menor lista para iniciar
    {
      id: SEED_IDS.so23,
      tenantId: TENANT_LOCAL,
      codigo: "OT-208",
      nombre: "Pruebas válvulas de seguridad fase 2",
      alcance:
        "Ejecución de 20 pruebas hidrostáticas a válvulas de seguridad del sistema de inyección, segunda fase del programa anual.",
      ubicacion: "Planta principal, CT-094",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 7_000,
      moneda: "USD",
      fechaInicio: "2026-07-01",
      fechaFin: "2026-08-15",
      fechaSolicitudCotizacion: "2026-05-01",
      fechaRecepcionCotizacion: "2026-05-10",
      fechaAprobacion: "2026-05-20",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-24: APROBADA — obra con avance ~50%
    {
      id: SEED_IDS.so24,
      tenantId: TENANT_LOCAL,
      codigo: "OT-209",
      nombre: "Reemplazo manifold CT-094",
      alcance:
        "Reemplazo completo del manifold del cluster CT-094: 25 niples, 80 uniones soldadas y pruebas hidrostáticas del conjunto.",
      ubicacion: "Cluster CT-094, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 80_900,
      moneda: "USD",
      fechaInicio: "2026-03-15",
      fechaFin: "2026-07-30",
      fechaSolicitudCotizacion: "2026-02-01",
      fechaRecepcionCotizacion: "2026-02-15",
      fechaAprobacion: "2026-03-01",
      fechaInicioEjecucion: "2026-03-18",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-25: APROBADA — otra obra con ~30% avance
    {
      id: SEED_IDS.so25,
      tenantId: TENANT_LOCAL,
      codigo: "OT-210",
      nombre: "Tendido FCS Aguada Norte fase 1",
      alcance:
        "Primera fase del tendido de FCS Aguada Norte: 480 ml de tubería de 4 pulg, 65 uniones soldadas y pruebas hidrostáticas.",
      ubicacion: "Pad Aguada Norte, Casanare",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 55_725,
      moneda: "USD",
      fechaInicio: "2026-04-10",
      fechaFin: "2026-08-30",
      fechaSolicitudCotizacion: "2026-02-20",
      fechaRecepcionCotizacion: "2026-03-05",
      fechaAprobacion: "2026-03-25",
      fechaInicioEjecucion: "2026-04-12",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-26: APROBADA — obra en curso
    {
      id: SEED_IDS.so26,
      tenantId: TENANT_LOCAL,
      codigo: "OT-211",
      nombre: "Línea inyección de agua CT-095",
      alcance:
        "Tendido y soldadura de 280 ml de tubería de 4 pulg para nuevo sistema de inyección en CT-095.",
      ubicacion: "Pozo CT-095, Casanare",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 40_120,
      moneda: "USD",
      fechaInicio: "2026-03-01",
      fechaFin: "2026-06-30",
      fechaSolicitudCotizacion: "2026-01-15",
      fechaRecepcionCotizacion: "2026-01-30",
      fechaAprobacion: "2026-02-15",
      fechaInicioEjecucion: "2026-03-03",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      notas:
        "Solicitud de reprogramación por hallazgo de tubería existente con corrosión avanzada en sector 2. RDs suspendidos hasta aprobación de nueva LB.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-27: APROBADA — obra al 90%, próxima a cierre
    {
      id: SEED_IDS.so27,
      tenantId: TENANT_LOCAL,
      codigo: "OT-212",
      nombre: "Soldadura masiva crítica Pad 4",
      alcance:
        "Soldadura crítica de 200 uniones para la conexión final del sistema de tratamiento Pad 4. Pruebas hidrostáticas certificadas por API 1104.",
      ubicacion: "Pad 4, Campo Aguada",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 21_700,
      moneda: "USD",
      fechaInicio: "2026-01-15",
      fechaFin: "2026-05-15",
      fechaSolicitudCotizacion: "2025-12-05",
      fechaRecepcionCotizacion: "2025-12-20",
      fechaAprobacion: "2026-01-08",
      fechaInicioEjecucion: "2026-01-18",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "APROBADA",
      notas:
        "Avance 90%. Pendiente entrega de dossier de soldaduras y certificados de prueba hidrostática para cerrar.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // OT-28: CERRADA — obra histórica completada, dossier entregado
    {
      id: SEED_IDS.so28,
      tenantId: TENANT_LOCAL,
      codigo: "OT-213",
      nombre: "Pruebas hidrostáticas Pad 3 (2025)",
      alcance:
        "Programa anual 2025 de pruebas hidrostáticas a 40 líneas de proceso del Pad 3. Cerrada con dossier completo.",
      ubicacion: "Pad 3, Campo Aguada",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 14_000,
      moneda: "USD",
      fechaInicio: "2025-09-01",
      fechaFin: "2025-12-15",
      fechaSolicitudCotizacion: "2025-07-15",
      fechaRecepcionCotizacion: "2025-07-30",
      fechaAprobacion: "2025-08-20",
      fechaInicioEjecucion: "2025-09-03",
      fechaCierre: "2025-12-22",
      aprobadoPor: "rafael.garcia@tecpetrol.com",
      estado: "CERRADA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },

    // OT-29: CANCELADA — aplazada por decisión del cliente
    {
      id: SEED_IDS.so29,
      tenantId: TENANT_LOCAL,
      codigo: "OT-214",
      nombre: "Tendido aplazado Pad 6",
      alcance:
        "Tendido de 150 ml de tubería en Pad 6. Aplazado indefinidamente por replanteo del cronograma de producción.",
      ubicacion: "Pad 6, Campo Aguada",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 12_750,
      moneda: "USD",
      fechaInicio: "2026-06-01",
      fechaFin: "2026-08-15",
      fechaSolicitudCotizacion: "2026-04-10",
      fechaRecepcionCotizacion: "2026-04-22",
      estado: "CANCELADA",
      motivoCancelacion:
        "Solicitud de aplazamiento indefinido por parte de Operaciones — replanteo del cronograma de producción del Pad 6.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "nestor.contreras@tecpetrol.com",
    },

    // OT-30: CANCELADA — conexión suspendida por hallazgo técnico
    {
      id: SEED_IDS.so30,
      tenantId: TENANT_LOCAL,
      codigo: "OT-215",
      nombre: "Conexión equipos rotativos suspendida",
      alcance:
        "Soldadura de conexión de 80 puntos para nuevos equipos rotativos. Suspendida por incompatibilidad de los equipos con la línea existente.",
      ubicacion: "Planta deshidratación CT-094",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A210002",
      contratoMarcoId: SEED_IDS.contrato2,
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      interventorId: "interventormecanico01@ingecontrol.com.co",
      monto: 9_360,
      moneda: "USD",
      fechaInicio: "2026-05-01",
      fechaFin: "2026-07-15",
      fechaSolicitudCotizacion: "2026-03-20",
      fechaRecepcionCotizacion: "2026-04-01",
      estado: "CANCELADA",
      motivoCancelacion:
        "Incompatibilidad técnica de equipos rotativos con sistema existente; ingeniería decidió rediseño de conexiones.",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "samir.perez@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
  ];
  // Enriquecer SOs con montoUsd/montoCop/trmCongelada usando la TRM del AFE padre.
  const sosEnriquecidas: So[] = sampleSos.map((s) => {
    const afe = sampleAfes.find((a) => a.id === s.afeId);
    return { ...s, ...congelarMonto(s.monto, s.moneda, afe?.trm ?? 0) };
  });
  if (soCount === 0) {
    // Prioriza el snapshot guardado por el ADMIN_PMIS (escenario de pruebas).
    // Si no hay snapshot, cae al baseline de código.
    await localDb.sos.bulkAdd(leerSosDefault() ?? sosEnriquecidas);
  }

  // ===== Tareas =====
  // OT-1 inline (3 tareas con IDs determinísticos para que las referencien
  // RDs/Actas del seed). Mismo esquema que mkTareas: tipo+seq+predecSeq+
  // tipoRel+lag+codigo=tarifa.codigo. Tarifas reales de contrato1 (Soforesta).
  const tMt = TARIFAS_INFO["tar-mt-001"]!;
  const tDr = TARIFAS_INFO["tar-dr-001"]!;
  const tPv = TARIFAS_INFO["tar-pv-001"]!;
  const sampleTareas: Tarea[] = [
    {
      id: SEED_IDS.tarea1,
      tenantId: TENANT_LOCAL,
      soId: so1Id,
      tipo: "TAREA",
      seq: 1,
      orden: 0,
      codigo: tMt.codigo,
      descripcion: "Movimiento de tierras",
      cantidad: 5000,
      unidad: tMt.unidad,
      tarifaId: "tar-mt-001",
      precioUnitario: tMt.precio,
      duracionDias: 30,
      fechaInicio: "2026-02-01",
      fechaFin: "2026-03-02",
      predecesoras: [],
      predecSeq: null,
      tipoRel: "FS",
      lag: 0,
      cantidadEjecutada: 4500,
      porcentajeAvance: 90,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.tarea2,
      tenantId: TENANT_LOCAL,
      soId: so1Id,
      tipo: "TAREA",
      seq: 2,
      orden: 1,
      codigo: tDr.codigo,
      descripcion: "Construcción drenajes",
      cantidad: 800,
      unidad: tDr.unidad,
      tarifaId: "tar-dr-001",
      precioUnitario: tDr.precio,
      duracionDias: 25,
      fechaInicio: "2026-02-20",
      fechaFin: "2026-03-15",
      predecesoras: [],
      predecSeq: 1,
      tipoRel: "FS",
      lag: 0,
      cantidadEjecutada: 600,
      porcentajeAvance: 75,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.tarea3,
      tenantId: TENANT_LOCAL,
      soId: so1Id,
      tipo: "TAREA",
      seq: 3,
      orden: 2,
      codigo: tPv.codigo,
      descripcion: "Pavimento plataforma",
      cantidad: 3500,
      unidad: tPv.unidad,
      tarifaId: "tar-pv-001",
      precioUnitario: tPv.precio,
      duracionDias: 20,
      fechaInicio: "2026-04-01",
      fechaFin: "2026-04-21",
      predecesoras: [],
      predecSeq: 2,
      tipoRel: "FS",
      lag: 0,
      cantidadEjecutada: 0,
      porcentajeAvance: 0,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },

    // ============ Tareas para SOs nuevas (so7-so15) ============
    // OT-7 EN_COTIZACION — borrador del PDT (sin avance)
    ...mkTareas(SEED_IDS.so7, [
      { tarifaId: "tar-mt-001", desc: "Movimiento tierras plataforma tanques", cant: 12000, ini: "2026-06-15", fin: "2026-07-30" },
      { tarifaId: "tar-dr-001", desc: "Drenajes perimetrales", cant: 600, ini: "2026-07-15", fin: "2026-08-30", predSeq: 1 },
    ]),

    // OT-8 EN_REVISION (PL2) — cotización completa enviada
    ...mkTareas(SEED_IDS.so8, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4 pulg sector A", cant: 1200, ini: "2026-06-01", fin: "2026-07-15" },
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4 pulg sector B", cant: 800, ini: "2026-06-15", fin: "2026-07-30" },
      { tarifaId: "tar-sd-001", desc: "Soldaduras de unión y empalme", cant: 180, ini: "2026-07-01", fin: "2026-08-15", predSeq: 1 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas", cant: 12, ini: "2026-08-15", fin: "2026-08-31", predSeq: 3 },
    ]),

    // OT-9 EN_COTIZACION (devuelta) — observaciones del PL2 al contratista
    ...mkTareas(SEED_IDS.so9, [
      { tarifaId: "tar-sd-001", desc: "Soldaduras de mantenimiento", cant: 80, ini: "2026-06-10", fin: "2026-07-10" },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas pos-mantenimiento", cant: 6, ini: "2026-07-05", fin: "2026-07-20", predSeq: 1 },
    ]),

    // OT-10 EN_REVISION (PL) — proyecto medio, con encadenamiento
    ...mkTareas(SEED_IDS.so10, [
      { tarifaId: "tar-mt-001", desc: "Excavación cimentación 3 tanques", cant: 4500, ini: "2026-07-01", fin: "2026-08-10" },
      { tarifaId: "tar-cn-001", desc: "Concreto estructural losas", cant: 950, ini: "2026-08-05", fin: "2026-09-25", predSeq: 1 },
      { tarifaId: "tar-pv-001", desc: "Pavimento perimetral", cant: 2000, ini: "2026-09-15", fin: "2026-10-30", predSeq: 2 },
    ]),

    // OT-11 EN_REVISION (PL) — OT de alto monto
    ...mkTareas(SEED_IDS.so11, [
      { tarifaId: "tar-mt-001", desc: "Excavación adicional tanque T-1", cant: 2000, ini: "2026-08-01", fin: "2026-08-30" },
      { tarifaId: "tar-cn-001", desc: "Concreto base losa T-1 a T-3", cant: 1800, ini: "2026-08-15", fin: "2026-10-30", predSeq: 1 },
      { tarifaId: "tar-dr-001", desc: "Drenajes perimetrales tanques", cant: 1200, ini: "2026-09-15", fin: "2026-11-15", predSeq: 2 },
      { tarifaId: "tar-pv-001", desc: "Pavimento área tanques", cant: 4000, ini: "2026-10-15", fin: "2026-12-10", predSeq: 3 },
    ]),

    // OT-12 APROBADA — lista para iniciar
    ...mkTareas(SEED_IDS.so12, [
      { tarifaId: "tar-sd-001", desc: "Recepción y posicionamiento bombas", cant: 4, ini: "2026-06-01", fin: "2026-06-15" },
      { tarifaId: "tar-tu-001", desc: "Tendido líneas conexión", cant: 350, ini: "2026-06-10", fin: "2026-07-20", predSeq: 1 },
      { tarifaId: "tar-pi-001", desc: "Pruebas funcionales y hidrostáticas", cant: 8, ini: "2026-07-15", fin: "2026-08-15", predSeq: 2 },
    ]),

    // OT-13 APROBADA — obra avanzada con avance parcial
    ...mkTareas(SEED_IDS.so13, [
      { tarifaId: "tar-pv-001", desc: "Pavimento extensión plataforma", cant: 1800, ini: "2026-03-15", fin: "2026-05-15", avance: 65 },
      { tarifaId: "tar-dr-001", desc: "Drenajes pluviales adicionales", cant: 200, ini: "2026-04-15", fin: "2026-06-15", predSeq: 1, avance: 30 },
      { tarifaId: "tar-cn-001", desc: "Concreto remates", cant: 50, ini: "2026-05-15", fin: "2026-06-30", predSeq: 2, avance: 0 },
    ]),

    // OT-14 APROBADA — todas las tareas al 100%
    ...mkTareas(SEED_IDS.so14, [
      { tarifaId: "tar-cn-001", desc: "Cimentación caseta", cant: 28, ini: "2026-01-15", fin: "2026-02-10", avance: 100 },
      { tarifaId: "tar-pv-001", desc: "Mampostería y muros", cant: 850, ini: "2026-02-05", fin: "2026-03-15", predSeq: 1, avance: 100 },
      { tarifaId: "tar-cn-001", desc: "Cubierta y acabados", cant: 24, ini: "2026-03-10", fin: "2026-04-25", predSeq: 2, avance: 100 },
    ]),

    // OT-15 CERRADA — todas al 100%
    ...mkTareas(SEED_IDS.so15, [
      { tarifaId: "tar-tu-001", desc: "Tendido cableado de fuerza", cant: 850, ini: "2026-01-10", fin: "2026-02-20", avance: 100 },
      { tarifaId: "tar-sd-001", desc: "Soldadura terminales y empalmes", cant: 120, ini: "2026-02-15", fin: "2026-03-15", predSeq: 1, avance: 100 },
      { tarifaId: "tar-pi-001", desc: "Pruebas dieléctricas y energización", cant: 4, ini: "2026-03-15", fin: "2026-03-28", predSeq: 2, avance: 100 },
    ]),

    // ========================================================================
    // Tareas de SOs 16-30 — Metálicas Brayan, todas con tarifas del contrato2:
    //   tar-tu-001  Tendido tubería 4 pulg  ($85/ml,  rend 60 ml/día)
    //   tar-sd-001  Soldadura tubería       ($45/u,   rend 12 u/día)
    //   tar-pi-001  Prueba hidrostática     ($350/u,  rend 1 u/día)
    // ========================================================================

    // OT-16 EN_COTIZACION (sin tareas — contratista pendiente de cotizar)

    // OT-17 EN_COTIZACION — cotización parcial, solo 1 tarea cargada
    ...mkTareas(SEED_IDS.so17, [
      { tarifaId: "tar-sd-001", desc: "Soldadura de 12 niples manifold", cant: 12, ini: "2026-06-15", fin: "2026-06-25" },
    ]),

    // OT-18 EN_REVISION (PL2) — pruebas hidrostáticas anuales
    ...mkTareas(SEED_IDS.so18, [
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas líneas producción", cant: 30, ini: "2026-07-01", fin: "2026-08-15" },
    ]),

    // OT-19 EN_REVISION (PL2) — soldadura conexiones FCS-008
    ...mkTareas(SEED_IDS.so19, [
      { tarifaId: "tar-sd-001", desc: "Soldadura 120 uniones FCS-008 → header", cant: 120, ini: "2026-07-15", fin: "2026-08-20" },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas tramos soldados", cant: 16, ini: "2026-08-15", fin: "2026-08-30", predSeq: 1 },
    ]),

    // OT-20 EN_REVISION (PL) — cambio de header inyección sector sur
    ...mkTareas(SEED_IDS.so20, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" header sector sur", cant: 430, ini: "2026-07-15", fin: "2026-08-31" },
      { tarifaId: "tar-sd-001", desc: "Soldadura uniones header", cant: 50, ini: "2026-08-15", fin: "2026-09-20", predSeq: 1 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas conjunto", cant: 28, ini: "2026-09-15", fin: "2026-10-15", predSeq: 2 },
    ]),

    // OT-21 EN_REVISION (PL) — recablado cluster norte CT-094
    ...mkTareas(SEED_IDS.so21, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" cluster norte", cant: 1200, ini: "2026-08-01", fin: "2026-10-15" },
      { tarifaId: "tar-sd-001", desc: "Soldadura 180 uniones cluster", cant: 180, ini: "2026-09-01", fin: "2026-11-15", predSeq: 1 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas aislamientos", cant: 60, ini: "2026-10-15", fin: "2026-12-10", predSeq: 2 },
      { tarifaId: "tar-pi-001", desc: "Pruebas finales integración cluster", cant: 6, ini: "2026-12-01", fin: "2026-12-15", predSeq: 3 },
    ]),

    // OT-22 APROBADA — tendido Pad 5 a Manifold
    ...mkTareas(SEED_IDS.so22, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" Pad 5 → Manifold", cant: 350, ini: "2026-06-15", fin: "2026-08-15" },
      { tarifaId: "tar-sd-001", desc: "Soldadura 40 uniones", cant: 40, ini: "2026-07-15", fin: "2026-09-10", predSeq: 1 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas Pad 5", cant: 6, ini: "2026-09-01", fin: "2026-09-30", predSeq: 2 },
    ]),

    // OT-23 APROBADA — pruebas válvulas seguridad fase 2
    ...mkTareas(SEED_IDS.so23, [
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas válvulas seguridad", cant: 20, ini: "2026-07-01", fin: "2026-08-15" },
    ]),

    // OT-24 APROBADA (~50% avance) — reemplazo manifold CT-094
    ...mkTareas(SEED_IDS.so24, [
      { tarifaId: "tar-sd-001", desc: "Soldadura niples manifold", cant: 25, ini: "2026-03-15", fin: "2026-05-10", avance: 88 },
      { tarifaId: "tar-sd-001", desc: "Soldadura uniones tramos principales", cant: 80, ini: "2026-04-15", fin: "2026-06-30", predSeq: 1, avance: 45 },
      { tarifaId: "tar-tu-001", desc: "Tendido conexiones laterales", cant: 240, ini: "2026-05-01", fin: "2026-07-10", predSeq: 1, avance: 35 },
      // Predec multi-FS no se modela: tomamos como predec la última crítica (tramos principales = seq 2)
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas final", cant: 16, ini: "2026-07-01", fin: "2026-07-30", predSeq: 2, avance: 0 },
    ]),

    // OT-25 APROBADA (~30% avance) — tendido Aguada Norte fase 1
    ...mkTareas(SEED_IDS.so25, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" Aguada Norte fase 1", cant: 480, ini: "2026-04-10", fin: "2026-07-15", avance: 55 },
      { tarifaId: "tar-sd-001", desc: "Soldadura 65 uniones fase 1", cant: 65, ini: "2026-06-01", fin: "2026-08-15", predSeq: 1, avance: 18 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas fase 1", cant: 12, ini: "2026-08-01", fin: "2026-08-30", predSeq: 2, avance: 0 },
    ]),

    // OT-26 APROBADA — línea CT-095
    ...mkTareas(SEED_IDS.so26, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" CT-095", cant: 280, ini: "2026-03-01", fin: "2026-05-15", avance: 40 },
      { tarifaId: "tar-sd-001", desc: "Soldadura uniones CT-095", cant: 35, ini: "2026-04-15", fin: "2026-06-15", predSeq: 1, avance: 22 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas CT-095", cant: 8, ini: "2026-06-01", fin: "2026-06-30", predSeq: 2, avance: 0 },
    ]),

    // OT-27 APROBADA — soldadura masiva Pad 4 (~90%)
    ...mkTareas(SEED_IDS.so27, [
      { tarifaId: "tar-sd-001", desc: "Soldadura 200 uniones críticas Pad 4", cant: 200, ini: "2026-01-15", fin: "2026-04-30", avance: 100 },
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas API 1104", cant: 36, ini: "2026-04-01", fin: "2026-05-15", predSeq: 1, avance: 80 },
    ]),

    // OT-28 CERRADA — pruebas hidrostáticas Pad 3 (2025, ejecutada 100%)
    ...mkTareas(SEED_IDS.so28, [
      { tarifaId: "tar-pi-001", desc: "Pruebas hidrostáticas líneas Pad 3", cant: 40, ini: "2025-09-01", fin: "2025-12-15", avance: 100 },
    ]),

    // OT-29 CANCELADA — tendido Pad 6 (aplazado)
    ...mkTareas(SEED_IDS.so29, [
      { tarifaId: "tar-tu-001", desc: "Tendido tubería 4\" Pad 6", cant: 150, ini: "2026-06-01", fin: "2026-07-30" },
    ]),

    // OT-30 CANCELADA — conexión equipos rotativos (suspendida)
    ...mkTareas(SEED_IDS.so30, [
      { tarifaId: "tar-sd-001", desc: "Soldadura 80 conexiones equipos rotativos", cant: 80, ini: "2026-05-01", fin: "2026-07-15" },
    ]),
  ];
  if (tareaCount === 0) {
    await localDb.tareas.bulkAdd(leerTareasDefault() ?? sampleTareas);
  }

  // ===== Usuarios (Personal real del PMIS Tecpetrol Colombia - bloque CPO-13) =====
  // Fuente: Registro_Autoridades_Simplificado.xlsx
  // El audit/auditoría se omite por brevedad: el seed usa "system" en createdBy/updatedBy.

  type SeedUser = Omit<Usuario, "id" | "tenantId" | "version" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy">;
  const mkUser = (id: string, u: SeedUser): Usuario => ({
    id,
    tenantId: TENANT_LOCAL,
    version: 1,
    createdAt: now,
    updatedAt: now,
    createdBy: "system",
    updatedBy: "system",
    ...u,
  });

  const sampleUsuarios: Usuario[] = [
    // ----- Administrador del PMIS (rol técnico con acceso total) -----
    mkUser("seed-user-admin-pmis", {
      email: "admin.pmis@tecpetrol.com",
      nombre: "Administrador PMIS",
      rol: "ADMIN_PMIS",
      empresa: "Tecpetrol Colombia",
      cargo: "Administrador del PMIS",
      activo: true,
      // Multi-Gerencia F1: ADMIN_PMIS ve todos por short-circuit aunque la lista
      // sea acotada. Declarar explícitamente CO + AR sirve de documentación
      // y como fallback si el short-circuit se removiera.
      gerenciasAccesibles: ["CO", "AR"],
    }),

    // ----- Personal Tecpetrol -----
    mkUser("seed-user-001-gte-fac", {
      email: "nahuel.allo@tecpetrol.com",
      nombre: "Nahuel Allo",
      rol: "GTE",
      cargo: "Facilities Sr. Manager",
      disciplina: "GENERAL",
      empresa: "Tecpetrol Colombia",
      activo: true,
      // El GTE opera regional — accede a Colombia y Argentina.
      gerenciasAccesibles: ["CO", "AR"],
    }),
    mkUser("seed-user-002-jf", {
      email: "nestor.contreras@tecpetrol.com",
      nombre: "Néstor Contreras",
      rol: "JEFE",
      cargo: "Project Manager",
      disciplina: "GENERAL",
      empresa: "Tecpetrol Colombia",
      activo: true,
      // El JEFE tiene visión regional — accede a Colombia y Argentina.
      gerenciasAccesibles: ["CO", "AR"],
    }),
    mkUser("seed-user-003-cfe", {
      email: "daniel.medina@tecpetrol.com",
      nombre: "Daniel Medina",
      rol: "CFE",
      rolesSecundarios: ["PL", "PL2"],
      cargo: "Controller Facilities Engineer",
      disciplina: "CIVIL",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-004-pl-i", {
      email: "samir.perez@tecpetrol.com",
      nombre: "Samir Pérez",
      rol: "PL",
      rolesSecundarios: ["PL2"],
      cargo: "Project Leader — Instrumentación",
      disciplina: "INSTRUMENTACION",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-005-pl-m1", {
      email: "eduardo.zambrano@tecpetrol.com",
      nombre: "Eduardo Zambrano",
      rol: "PL",
      rolesSecundarios: ["PL2"],
      cargo: "Project Leader — Mecánica",
      disciplina: "MECANICA",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-006-pl2-m", {
      email: "rafael.garcia@tecpetrol.com",
      nombre: "Rafael García",
      rol: "PL2",
      // ADMIN_PMIS como rol secundario: Rafael puede entrar como PL2
      // (operación diaria) o elegir ADMIN_PMIS en el login (administración
      // del PMIS, requiere la segunda clave). Ver flujo en LocalAuthProvider.
      rolesSecundarios: ["ADMIN_PMIS"],
      cargo: "Project Leader 2 — Mecánica / Administrador del PMIS",
      disciplina: "MECANICA",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-007-pl-e", {
      email: "german.bolanos@tecpetrol.com",
      nombre: "Germán Bolaños",
      rol: "PL",
      rolesSecundarios: ["PL2"],
      cargo: "Project Leader — Eléctrica",
      disciplina: "ELECTRICA",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-008-pl2-e", {
      email: "sebastian.laverde@tecpetrol.com",
      nombre: "Sebastián Laverde",
      rol: "PL2",
      rolesSecundarios: [],
      cargo: "Project Leader 2 — Eléctrica",
      disciplina: "ELECTRICA",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-009-pl-c", {
      email: "franklin.torres@tecpetrol.com",
      nombre: "Franklin Alfonso Torres Morales",
      rol: "PL",
      rolesSecundarios: ["PL2"],
      cargo: "Project Leader — Civil",
      disciplina: "CIVIL",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-010-abas", {
      email: "francisco.herrera@tecpetrol.com",
      nombre: "Francisco Herrera",
      rol: "ABAS",
      cargo: "Procurement Lead Analyst",
      disciplina: "GENERAL",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-011-abas", {
      email: "diana.ruiz@tecpetrol.com",
      nombre: "Diana Ruiz",
      rol: "ABAS",
      cargo: "Procurement Buyer — Equipos rotativos",
      disciplina: "MECANICA",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-012-abas", {
      email: "laura.mendez@tecpetrol.com",
      nombre: "Laura Méndez",
      rol: "ABAS",
      cargo: "Procurement Buyer — Materiales civiles",
      disciplina: "CIVIL",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-013-abas", {
      email: "jose.salazar@tecpetrol.com",
      nombre: "José Salazar",
      rol: "ABAS",
      cargo: "Procurement Buyer — Servicios y contratos marco",
      disciplina: "GENERAL",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-almacen-001", {
      email: "luis.almacen@tecpetrol.com",
      nombre: "Luis Romero",
      rol: "ALMACEN",
      cargo: "Coordinador de Almacén CPO13",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    // ----- Roles operativos internos (Producción / Mantenimiento / Comisionamiento) -----
    // Reciben las facilidades al cierre (firman listado de pendientes), registran
    // eventos en su área y consultan actas/SOs de las obras que reciben.
    mkUser("seed-user-oper-001", {
      email: "carlos.produccion@tecpetrol.com",
      nombre: "Carlos Pinto",
      rol: "OPER",
      cargo: "Supervisor de Producción CPO13",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-mtto-001", {
      email: "andres.mtto@tecpetrol.com",
      nombre: "Andrés Cárdenas",
      rol: "MTTO",
      cargo: "Supervisor de Mantenimiento CPO13",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),
    mkUser("seed-user-comm-001", {
      email: "diego.comisionamiento@tecpetrol.com",
      nombre: "Diego Linares",
      rol: "COMM",
      cargo: "Autoridad de Comisionamiento CPO13",
      empresa: "Tecpetrol Colombia",
      activo: true,
    }),

    // ----- Interventor externo: Ingecontrol S.A. (PO 24199) -----
    mkUser("seed-user-int-101", {
      email: "adrianaguaqueta@ingecontrol.com.co",
      nombre: "Adriana Elizabeth Guaqueta",
      rol: "ADOR",
      cargo: "Directora de Proyectos",
      telefono: "+57 312 4545683",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-102", {
      email: "alvarojaramillo@ingecontrol.com.co",
      nombre: "Álvaro Jaramillo",
      rol: "PLANNER",
      cargo: "Coordinador de Proyectos",
      telefono: "+57 300 6125133",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-103", {
      email: "ligiacamacho@ingecontrol.com.co",
      nombre: "Ligia Constanza Camacho",
      rol: "ADOR",
      cargo: "COO / VP de Operaciones",
      telefono: "+57 312 7839563",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-104", {
      email: "mariacalderon@ingecontrol.com.co",
      nombre: "María Rosa Calderón",
      rol: "PLANNER",
      cargo: "Gerente Administrativa",
      telefono: "+57 300 8163383",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-105", {
      email: "fredyvanegas@ingecontrol.com.co",
      nombre: "Fredy H. Vanegas C.",
      rol: "INT",
      cargo: "Líder Interventoría Civil",
      telefono: "+57 311 5823546",
      disciplina: "CIVIL",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-106", {
      email: "interventorcivil@ingecontrol.com.co",
      nombre: "Harvin Albeiro Aguilar Gómez",
      rol: "INT",
      cargo: "Interventor Civil",
      telefono: "+57 315 6260943",
      disciplina: "CIVIL",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-107", {
      email: "interventormecanico01@ingecontrol.com.co",
      nombre: "Carlos Mayor",
      rol: "INT",
      cargo: "Interventor Mecánica",
      telefono: "+57 323 2840623",
      disciplina: "MECANICA",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-108", {
      email: "dannyleon@ingecontrol.com.co",
      nombre: "Danny Libardo León Gómez",
      rol: "INT",
      cargo: "Interventor Mecánica",
      telefono: "+57 316 5396905",
      disciplina: "MECANICA",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-109", {
      email: "interventorelectrico@ingecontrol.com.co",
      nombre: "Luis F. Hernández Rodríguez",
      rol: "INT",
      cargo: "Interventor Eléctrica",
      telefono: "+57 311 4562066",
      disciplina: "ELECTRICA",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),
    mkUser("seed-user-int-110", {
      email: "giovannisarmiento@ingecontrol.com.co",
      nombre: "Giovanni Sarmiento Forero",
      rol: "INT",
      cargo: "Interventor Eléctrica",
      telefono: "+57 311 4562066",
      disciplina: "ELECTRICA",
      empresa: "Ingecontrol S.A.",
      contratistaId: "ingecontrol",
      activo: true,
    }),

    // ----- Contratista: Soforesta S.A.S. (PO 31083 - Obras Civiles) -----
    mkUser("seed-user-sofo-201", {
      email: "Directorcp013@soforesta.com.co",
      nombre: "Joaquín González",
      rol: "ADOR",
      cargo: "Director de Obra",
      telefono: "+57 316 6201905",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-202", {
      email: "residentecp013@soforesta.com.co",
      nombre: "Camilo Africano",
      rol: "ADOR",
      cargo: "Residente Obra",
      telefono: "+57 313 4427299",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-203", {
      email: "programadorcp013@soforesta.com.co",
      nombre: "José Javier Andrade",
      rol: "PLANNER",
      cargo: "Programador Obra",
      telefono: "+57 320 6572141",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-204", {
      email: "admoncp013@soforesta.com.co",
      nombre: "Brayan Ladino",
      rol: "ADOR",
      cargo: "Administrador Obra",
      telefono: "+57 311 5178233",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-205", {
      email: "hsetecpetrol@soforesta.com.co",
      nombre: "Sebastián Castro",
      rol: "ADOR",
      cargo: "Coordinador HSE",
      telefono: "+57 310 2217302",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-206", {
      email: "j.millan@soforesta.com.co",
      nombre: "Jaime Millán",
      rol: "PLANNER",
      cargo: "Coordinador Soforesta",
      telefono: "+57 310 6195476",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),
    mkUser("seed-user-sofo-207", {
      email: "gerencia@soforesta.com.co",
      nombre: "Humberto Millán",
      rol: "ADOR",
      cargo: "Gerencia Soforesta",
      telefono: "+57 317 4004583",
      disciplina: "CIVIL",
      empresa: "Soforesta S.A.S.",
      contratistaId: "soforesta",
      activo: true,
    }),

    // ----- Contratista: Bobinados Electro Oriente S.A.S. (POs 35587, 24067, 31885) -----
    mkUser("seed-user-beo-301", {
      email: "proyectos@beoriente.com",
      nombre: "Hollman Esteewar Lagos Núñez",
      rol: "ADOR",
      cargo: "Líder de Proyectos",
      telefono: "+57 310 2787898",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),
    mkUser("seed-user-beo-302", {
      email: "licitaciones@beoriente.com",
      nombre: "Lisseth Adriana Ortiz Cabullo",
      rol: "ADOR",
      cargo: "Ingeniera de Proyectos",
      telefono: "+57 313 3620724",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),
    mkUser("seed-user-beo-303", {
      email: "qhse@beoriente.com",
      nombre: "William Felipe Gámez Acevedo",
      rol: "ADOR",
      cargo: "Líder QHSE",
      telefono: "+57 310 5778307",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),
    mkUser("seed-user-beo-304", {
      email: "gestionproyectos@beoriente.com",
      nombre: "Angie Lorena Suárez Plazas",
      rol: "ADOR",
      cargo: "Ingeniera Residente",
      telefono: "+57 313 4728990",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),
    mkUser("seed-user-beo-305", {
      email: "ingenieria@beoriente.com",
      nombre: "Jimmy Alexander Africano Gallego",
      rol: "ADOR",
      cargo: "Ingeniero Residente",
      telefono: "+57 320 8612040",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),
    mkUser("seed-user-beo-306", {
      email: "calidadtecpetrol@beoriente.com",
      nombre: "Jhon Jairo Pantoja Trigos",
      rol: "PLANNER",
      cargo: "Ingeniero QA/QC",
      telefono: "+57 313 2891810",
      disciplina: "ELECTRICA",
      empresa: "Bobinados Electro Oriente S.A.S.",
      contratistaId: "beoriente",
      activo: true,
    }),

    // ----- Contratista: Metálicas Brayan SAS (PO 48418 - Obras Mecánicas) -----
    mkUser("seed-user-mb-401", {
      email: "dgmolina21@hotmail.com",
      nombre: "Daniel Guanumen Molina",
      rol: "PLANNER",
      cargo: "Director de Proyecto / Control Obra",
      telefono: "+57 315 8635282",
      disciplina: "MECANICA",
      empresa: "Metálicas Brayan SAS",
      contratistaId: "metalicas-brayan",
      activo: true,
    }),
    mkUser("seed-user-mb-402", {
      email: "metalicasbrayan@hotmail.com",
      nombre: "Yahahiny Betancourt",
      rol: "ADOR",
      cargo: "Administradora de Contrato",
      telefono: "+57 320 2078284",
      disciplina: "MECANICA",
      empresa: "Metálicas Brayan SAS",
      contratistaId: "metalicas-brayan",
      activo: true,
    }),
    mkUser("seed-user-mb-403", {
      email: "hseqmetalicasbrayansas@gmail.com",
      nombre: "Miguel Cristancho",
      rol: "ADOR",
      cargo: "Coordinador HSE",
      telefono: "+57 321 4155095",
      disciplina: "MECANICA",
      empresa: "Metálicas Brayan SAS",
      contratistaId: "metalicas-brayan",
      activo: true,
    }),
  ];
  // Garantía mínima de personal: SIEMPRE asegura que cada usuario del seed
  // exista en la base, sin pisar customizaciones del ADMIN sobre el mismo id.
  //   - Si un id `seed-user-*` ya está en la tabla → no se toca (respeta
  //     ediciones manuales de email, rol, teléfono, etc.).
  //   - Si falta → se inserta el del seed.
  // Esto sobrevive a la confusión histórica de tenants y a borrados
  // accidentales: el listado de personal nunca queda vacío.
  const idsSeed = sampleUsuarios.map((u) => u.id);
  const existentes = new Set<string>(
    (await localDb.usuarios.where("id").anyOf(idsSeed).primaryKeys()) as string[],
  );
  const faltantes = sampleUsuarios.filter((u) => !existentes.has(u.id));
  if (faltantes.length > 0) {
    await localDb.usuarios.bulkPut(faltantes);
    console.info(`[seed personal] ${faltantes.length} usuarios canónicos restaurados.`);
  }

  // ===== Eventos =====
  const sampleEventos: Evento[] = [
    {
      id: SEED_IDS.evento1,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "HSE",
      fecha: "2026-05-02",
      descripcion: "Casi-incidente: caida de objeto desde plataforma. Sin lesionados.",
      reportadoPor: "interventor@externo.com",
      adjuntos: [],
      metadatos: { gravedad: "media" },
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "interventor@externo.com",
      updatedBy: "interventor@externo.com",
    },
    {
      id: SEED_IDS.evento2,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "CALIDAD",
      fecha: "2026-04-15",
      descripcion: "Compactacion fuera de especificacion en zona norte. Reproceso requerido.",
      reportadoPor: "interventor@externo.com",
      adjuntos: [],
      metadatos: { ncrId: "NCR-2026-001" },
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "interventor@externo.com",
      updatedBy: "interventor@externo.com",
    },
    {
      id: SEED_IDS.evento3,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "FOTO",
      fecha: "2026-05-05",
      descripcion: "Avance plataforma 90% movimiento de tierras",
      reportadoPor: "contratista@externo.com",
      adjuntos: ["foto-001.jpg"],
      metadatos: {},
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "contratista@externo.com",
    },
    {
      id: SEED_IDS.evento4,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "AMBIENTAL",
      fecha: "2026-04-22",
      descripcion: "Hallazgo de fauna silvestre en zona de obra. Reubicacion exitosa.",
      reportadoPor: "contratista@externo.com",
      adjuntos: [],
      metadatos: { especie: "ardilla", accion: "reubicacion" },
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "contratista@externo.com",
    },
    {
      id: SEED_IDS.evento5,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "LECCION",
      fecha: "2026-05-01",
      descripcion: "Mejora continua: rotacion de equipos para reducir tiempos muertos en frentes paralelos.",
      reportadoPor: "daniel.medina@tecpetrol.com",
      adjuntos: [],
      metadatos: { categoria: "productividad" },
      // Fase 4 del ADR — lección compartida cross-gerencia (productividad
      // de proveedores, aplicable en cualquier operación).
      compartido: true,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "daniel.medina@tecpetrol.com",
      updatedBy: "daniel.medina@tecpetrol.com",
    },
    {
      id: SEED_IDS.evento6,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so2,
      tipo: "SEG_FISICA",
      fecha: "2026-05-06",
      descripcion: "Personal externo no autorizado intento ingresar al campamento. Seguridad lo retiro.",
      reportadoPor: "interventor@externo.com",
      adjuntos: [],
      metadatos: { gravedad: "baja" },
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "interventor@externo.com",
      updatedBy: "interventor@externo.com",
    },
    {
      id: SEED_IDS.evento7,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      tipo: "LEAN",
      fecha: "2026-04-28",
      descripcion: "Implementada nueva ruta de carga reduciendo tiempo muerto en 15%.",
      reportadoPor: "eduardo.zambrano@tecpetrol.com",
      adjuntos: [],
      metadatos: { ahorroEstimado: "15%" },
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "eduardo.zambrano@tecpetrol.com",
      updatedBy: "eduardo.zambrano@tecpetrol.com",
    },
  ];
  if (eventoCount === 0) await localDb.eventos.bulkAdd(sampleEventos);

  // ===== Reportes Diarios (4 ejemplos en distintos estados §5.6) =====
  // Modelo simplificado: BORRADOR / EN_REVISION (sub-flag INT|PL) / APROBADO / CANCELADO.
  const sampleRds: Rd[] = [
    {
      id: SEED_IDS.rd1,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      fecha: "2026-05-05",
      estado: "APROBADO",
      nivelRevision: "PL",
      // Calificación INT: 3★ por algunos rechazos puntuales del Interventor.
      calificacionInt: 3,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Jornada normal sin novedades",
      },
      avancesTareas: [
        { tareaId: SEED_IDS.tarea1, cantidadDia: 250, porcentajeDia: 5 },
        { tareaId: SEED_IDS.tarea2, cantidadDia: 40, porcentajeDia: 5 },
      ],
      recursos: {
        maqAmarilla: [
          { equipo: "Bulldozer sobre orugas", cantidad: 2, horas: 8, galones: 112 },
          { equipo: "Retro Excavadora sobre oruga", cantidad: 1, horas: 8, galones: 40 },
        ],
        maqHerramientas: [{ equipo: "Compresor aire", cantidad: 1, horas: 6, galones: 22.2 }],
        vehMovilizacion: [{ equipo: "Camioneta 4x4", cantidad: 3, km: 60, galones: 6 }],
        vehCarga: [{ equipo: "Volqueta", cantidad: 2, km: 80, galones: 26.67 }],
        personal: [
          { rol: "Maestro de obra", origen: "Local", clase: "STAFF", cantidad: 2 },
          { rol: "Operador maquinaria", origen: "Foraneo", clase: "MOC", cantidad: 6 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 12 },
        ],
        herramientas: [
          { herramienta: "Pala", cantidad: 10 },
          { herramienta: "Pico", cantidad: 8 },
        ],
      },
      eventos: [SEED_IDS.evento3],
      firmaSha256: "abc123def456789...",
      observacionesInt: "Aprobado sin observaciones",
      observacionesPl2: "Conforme",
      aprobadoPorInt: "interventor@externo.com",
      aprobadoPorPl2: "franklin.torres@tecpetrol.com",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd2,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      fecha: "2026-05-06",
      // INT lo aprobó (calificación 4★) y lo escaló al PL para integración.
      estado: "EN_REVISION",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 2,
        horasTormenta: 0,
        condicionClima: "Lluvia",
        jornadaEfectivaHoras: 7,
      },
      avancesTareas: [{ tareaId: SEED_IDS.tarea1, cantidadDia: 180, porcentajeDia: 3.6 }],
      recursos: {
        maqAmarilla: [{ equipo: "Bulldozer sobre orugas", cantidad: 2, horas: 6, galones: 84 }],
        maqHerramientas: [],
        vehMovilizacion: [{ equipo: "Camioneta 4x4", cantidad: 3, km: 50, galones: 5 }],
        vehCarga: [{ equipo: "Volqueta", cantidad: 2, km: 60, galones: 20 }],
        personal: [
          { rol: "Operador maquinaria", origen: "Foraneo", clase: "MOC", cantidad: 4 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 10 },
        ],
        herramientas: [{ herramienta: "Pala", cantidad: 8 }],
      },
      eventos: [],
      observacionesInt: "OK, jornada reducida por lluvia",
      aprobadoPorInt: "interventor@externo.com",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "interventor@externo.com",
    },
    {
      id: SEED_IDS.rd3,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      fecha: "2026-05-07",
      // Recién enviado por el contratista, pendiente de revisión del INT.
      estado: "EN_REVISION",
      nivelRevision: "INT",
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Nublado",
        jornadaEfectivaHoras: 9,
      },
      avancesTareas: [
        { tareaId: SEED_IDS.tarea1, cantidadDia: 320, porcentajeDia: 6.4 },
        { tareaId: SEED_IDS.tarea2, cantidadDia: 50, porcentajeDia: 6.25 },
      ],
      recursos: {
        maqAmarilla: [{ equipo: "Bulldozer sobre orugas", cantidad: 2, horas: 8, galones: 112 }],
        maqHerramientas: [{ equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 }],
        vehMovilizacion: [{ equipo: "Camioneta 4x4", cantidad: 4, km: 70, galones: 9.33 }],
        vehCarga: [{ equipo: "Volqueta", cantidad: 3, km: 90, galones: 45 }],
        personal: [
          { rol: "Maestro de obra", origen: "Local", clase: "STAFF", cantidad: 2 },
          { rol: "Operador maquinaria", origen: "Foraneo", clase: "MOC", cantidad: 6 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 14 },
        ],
        herramientas: [
          { herramienta: "Pala", cantidad: 12 },
          { herramienta: "Pico", cantidad: 10 },
        ],
      },
      eventos: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "contratista@externo.com",
    },
    {
      id: SEED_IDS.rd4,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so1,
      fecha: "2026-05-08",
      // Borrador en captura del contratista.
      estado: "BORRADOR",
      jornada: {
        horaInicio: "07:00",
        horaFin: "12:00",
        descansosMinutos: 30,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 4.5,
        observaciones: "Jornada parcial - en proceso de captura",
      },
      avancesTareas: [],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [],
        vehMovilizacion: [],
        vehCarga: [],
        personal: [],
        herramientas: [],
      },
      eventos: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "contratista@externo.com",
    },
    // ===== RDs de simulación de desempeño (calificación INT) =====
    // Estos RDs alimentan la media de calificación de cada Contrato Marco
    // mostrada en la tabla del módulo "Contratos Marco" (col. Desempeño).
    //
    //   contrato1 (Soforesta):  rd1=3★ + rd2=4★ → media 3.5★
    //   contrato2 (Serv. Ind. XYZ):  rd5=4★      → media 4.0★
    //   contrato3 (Log. del Llano):  rd6=5★ + rd7=4★ → media 4.5★
    //
    // VD §6.7.4: la calificación 1-5★ la propone el INT al aprobar el RD y
    // queda visible para el PL/PL2 cuando revisan antes de integrar al PMIS.
    {
      id: SEED_IDS.rd5,
      tenantId: TENANT_LOCAL,
      // Movido a so24 (APROBADA, Mecánica) — el RD solo existe sobre SOs aprobadas.
      soId: SEED_IDS.so24,
      fecha: "2026-05-04",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Avance normal en montaje mecánico",
      },
      avancesTareas: [],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 40, galones: 4 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Soldador certificado", origen: "Foraneo", clase: "MOC", cantidad: 3 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 5 },
        ],
        herramientas: [{ herramienta: "Equipo de soldadura", cantidad: 3 }],
      },
      eventos: [],
      observacionesInt: "Cumplimiento del plan, sin novedades técnicas.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      aprobadoPorPl2: "rafael.garcia@tecpetrol.com",
      firmaSha256: "fc1d5e8a9b2c4f6h...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista-002@externo.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd6,
      tenantId: TENANT_LOCAL,
      // Movido a so1 (APROBADA, Civil) — el RD solo existe sobre SOs aprobadas.
      soId: SEED_IDS.so1,
      fecha: "2026-04-20",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 5,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Topografía completada con precisión sub-centimétrica.",
      },
      avancesTareas: [],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 1, km: 80, galones: 8 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Topógrafo", origen: "Local", clase: "STAFF", cantidad: 1 },
          { rol: "Cadenero", origen: "Local", clase: "MONC", cantidad: 2 },
        ],
        herramientas: [
          { herramienta: "Estación total GNSS", cantidad: 1 },
          { herramienta: "Trípode topográfico", cantidad: 2 },
        ],
      },
      eventos: [],
      observacionesInt:
        "Excelente desempeño — entrega completa antes del plazo y con calidad sobresaliente.",
      aprobadoPorInt: "fredyvanegas@ingecontrol.com.co",
      aprobadoPorPl2: "franklin.torres@tecpetrol.com",
      firmaSha256: "9af3e1c2b5d8e0a7...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista-003@externo.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd7,
      tenantId: TENANT_LOCAL,
      // Movido a so1 (APROBADA, Civil) — el RD solo existe sobre SOs aprobadas.
      soId: SEED_IDS.so1,
      fecha: "2026-04-22",
      // INT ya calificó 4★ y escaló al PL para integración.
      estado: "EN_REVISION",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "16:00",
        descansosMinutos: 60,
        horasLluvia: 1,
        horasTormenta: 0,
        condicionClima: "Nublado",
        jornadaEfectivaHoras: 7,
        observaciones: "Cierre del levantamiento; pequeña pérdida por lluvia.",
      },
      avancesTareas: [],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 1, km: 60, galones: 6 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Topógrafo", origen: "Local", clase: "STAFF", cantidad: 1 },
          { rol: "Cadenero", origen: "Local", clase: "MONC", cantidad: 2 },
        ],
        herramientas: [{ herramienta: "Estación total GNSS", cantidad: 1 }],
      },
      eventos: [],
      observacionesInt: "Buen cierre, jornada acortada por lluvia justificada.",
      aprobadoPorInt: "fredyvanegas@ingecontrol.com.co",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista-003@externo.com",
      updatedBy: "fredyvanegas@ingecontrol.com.co",
    },

    // ========================================================================
    // RDs de SOs Metálicas Brayan — cobertura de estados del módulo RD
    // ========================================================================
    //   rd8  → so24 APROBADO (con firma)
    //   rd9  → so24 EN_REVISION (nivelRevision PL)
    //   rd10 → so24 EN_REVISION (nivelRevision INT)
    //   rd11 → so25 APROBADO
    //   rd12 → so25 BORRADOR (contratista capturando)
    //   rd13 → so27 APROBADO (avance casi total)
    //   rd14 → so27 EN_REVISION (nivelRevision PL)
    //   rd15 → so28 APROBADO (histórico, contrato cerrado)

    // ----- so24 (reemplazo manifold CT-094, APROBADA ~50% avance) -----
    {
      id: SEED_IDS.rd8,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so24,
      fecha: "2026-04-12",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Avance normal en soldadura de niples.",
      },
      avancesTareas: [
        { tareaId: "seed-tarea-024-T-001", cantidadDia: 8, porcentajeDia: 32 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 35, galones: 3.5 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Soldador certificado 6G", origen: "Foraneo", clase: "MOC", cantidad: 3 },
          { rol: "Ayudante de soldadura", origen: "Local", clase: "MONC", cantidad: 3 },
        ],
        herramientas: [{ herramienta: "Equipo de soldadura", cantidad: 3 }],
      },
      eventos: [],
      observacionesInt: "Cumplimiento del plan. Calidad de soldadura conforme.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      aprobadoPorPl2: "rafael.garcia@tecpetrol.com",
      firmaSha256: "8b3f1e2c5a9d4e7h2i6j...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd9,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so24,
      fecha: "2026-05-08",
      // INT ya calificó, escaló al PL — el PL revisa antes de integrar.
      estado: "EN_REVISION",
      nivelRevision: "PL",
      calificacionInt: 5,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Soldadura tramos principales avanza muy bien.",
      },
      avancesTareas: [
        { tareaId: "seed-tarea-024-T-002", cantidadDia: 10, porcentajeDia: 12.5 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 1, horas: 9, galones: 33.3 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 40, galones: 4 },
        ],
        vehCarga: [{ equipo: "Volqueta", cantidad: 1, km: 25, galones: 8.3 }],
        personal: [
          { rol: "Soldador certificado 6G", origen: "Foraneo", clase: "MOC", cantidad: 4 },
          { rol: "Ayudante de soldadura", origen: "Local", clase: "MONC", cantidad: 4 },
          { rol: "Inspector NDT", origen: "Foraneo", clase: "STAFF", cantidad: 1 },
        ],
        herramientas: [
          { herramienta: "Equipo de soldadura", cantidad: 4 },
          { herramienta: "Tintas penetrantes NDT", cantidad: 6 },
        ],
      },
      eventos: [],
      observacionesInt:
        "Rendimiento sobresaliente — 10 uniones con cero rechazos en inspección NDT.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "interventormecanico01@ingecontrol.com.co",
    },
    {
      id: SEED_IDS.rd10,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so24,
      fecha: "2026-05-09",
      // Recién enviado por el contratista — pendiente revisión del INT.
      estado: "EN_REVISION",
      nivelRevision: "INT",
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
      },
      avancesTareas: [
        { tareaId: "seed-tarea-024-T-002", cantidadDia: 8, porcentajeDia: 10 },
        { tareaId: "seed-tarea-024-T-003", cantidadDia: 25, porcentajeDia: 10.4 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 30, galones: 3 },
        ],
        vehCarga: [{ equipo: "Volqueta", cantidad: 1, km: 20, galones: 6.7 }],
        personal: [
          { rol: "Soldador certificado 6G", origen: "Foraneo", clase: "MOC", cantidad: 3 },
          { rol: "Tubero", origen: "Local", clase: "MOC", cantidad: 2 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 4 },
        ],
        herramientas: [{ herramienta: "Equipo de soldadura", cantidad: 3 }],
      },
      eventos: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // ----- so25 (tendido Aguada Norte fase 1, APROBADA ~30%) -----
    {
      id: SEED_IDS.rd11,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so25,
      fecha: "2026-05-05",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "06:30",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0.5,
        horasTormenta: 0,
        condicionClima: "Parcialmente nublado",
        jornadaEfectivaHoras: 9,
        observaciones: "Tendido normal, leve lluvia al medio día.",
      },
      avancesTareas: [
        { tareaId: "seed-tarea-025-T-001", cantidadDia: 60, porcentajeDia: 12.5 },
      ],
      recursos: {
        maqAmarilla: [{ equipo: "Retro Excavadora sobre oruga", cantidad: 1, horas: 8, galones: 40 }],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 1, horas: 6, galones: 22.2 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 80, galones: 8 },
        ],
        vehCarga: [{ equipo: "Volqueta", cantidad: 2, km: 80, galones: 26.7 }],
        personal: [
          { rol: "Operador maquinaria", origen: "Foraneo", clase: "MOC", cantidad: 2 },
          { rol: "Tubero", origen: "Local", clase: "MOC", cantidad: 3 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 6 },
        ],
        herramientas: [
          { herramienta: "Equipo de soldadura", cantidad: 2 },
          { herramienta: "Pala", cantidad: 6 },
        ],
      },
      eventos: [],
      observacionesInt: "Avance dentro del plan; rendimiento del tendido en línea.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      aprobadoPorPl2: "rafael.garcia@tecpetrol.com",
      firmaSha256: "a4c2f5d8e1b3c9a6h7j8...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd12,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so25,
      fecha: "2026-05-10",
      estado: "BORRADOR",
      jornada: {
        horaInicio: "07:00",
        horaFin: "13:00",
        descansosMinutos: 30,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 5.5,
        observaciones: "Jornada parcial — contratista todavía capturando avances.",
      },
      avancesTareas: [],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [],
        vehMovilizacion: [],
        vehCarga: [],
        personal: [],
        herramientas: [],
      },
      eventos: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "metalicasbrayan@hotmail.com",
    },

    // ----- so27 (soldadura masiva Pad 4, APROBADA ~90%) -----
    {
      id: SEED_IDS.rd13,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so27,
      fecha: "2026-04-25",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 5,
      jornada: {
        horaInicio: "07:00",
        horaFin: "18:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 10,
        observaciones: "Cierre de soldaduras críticas. Cero rechazos NDT.",
      },
      avancesTareas: [
        { tareaId: "seed-tarea-027-T-001", cantidadDia: 14, porcentajeDia: 7 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [
          { equipo: "Compresor aire", cantidad: 2, horas: 10, galones: 74 },
        ],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 2, km: 40, galones: 4 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Soldador certificado 6G", origen: "Foraneo", clase: "MOC", cantidad: 6 },
          { rol: "Inspector NDT", origen: "Foraneo", clase: "STAFF", cantidad: 1 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 6 },
        ],
        herramientas: [{ herramienta: "Equipo de soldadura", cantidad: 6 }],
      },
      eventos: [],
      observacionesInt: "Excelente. Calidad superior, ritmo sostenido.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      aprobadoPorPl2: "rafael.garcia@tecpetrol.com",
      firmaSha256: "e7d3c1f8a4b9e2c5h6j1...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    {
      id: SEED_IDS.rd14,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so27,
      fecha: "2026-05-06",
      estado: "EN_REVISION",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "16:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 8,
      },
      avancesTareas: [
        { tareaId: "seed-tarea-027-T-002", cantidadDia: 6, porcentajeDia: 16.7 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [{ equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 }],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 1, km: 30, galones: 3 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Inspector hidrostática", origen: "Foraneo", clase: "STAFF", cantidad: 1 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 2 },
        ],
        herramientas: [{ herramienta: "Bomba prueba hidrostática", cantidad: 1 }],
      },
      eventos: [],
      observacionesInt:
        "Pruebas hidrostáticas a 1.5× presión de diseño según API 1104, sin novedades.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "interventormecanico01@ingecontrol.com.co",
    },

    // ----- so28 (Pad 3 pruebas hidrostáticas, CERRADA histórico) -----
    {
      id: SEED_IDS.rd15,
      tenantId: TENANT_LOCAL,
      soId: SEED_IDS.so28,
      fecha: "2025-11-20",
      estado: "APROBADO",
      nivelRevision: "PL",
      calificacionInt: 4,
      jornada: {
        horaInicio: "07:00",
        horaFin: "17:00",
        descansosMinutos: 60,
        horasLluvia: 0,
        horasTormenta: 0,
        condicionClima: "Soleado",
        jornadaEfectivaHoras: 9,
        observaciones: "Jornada del cierre del programa anual 2025.",
      },
      avancesTareas: [
        { tareaId: "seed-tarea-028-T-001", cantidadDia: 4, porcentajeDia: 10 },
      ],
      recursos: {
        maqAmarilla: [],
        maqHerramientas: [{ equipo: "Compresor aire", cantidad: 1, horas: 8, galones: 29.6 }],
        vehMovilizacion: [
          { equipo: "Camioneta 4x4", cantidad: 1, km: 50, galones: 5 },
        ],
        vehCarga: [],
        personal: [
          { rol: "Inspector hidrostática", origen: "Foraneo", clase: "STAFF", cantidad: 1 },
          { rol: "Ayudante", origen: "Local", clase: "MONC", cantidad: 2 },
        ],
        herramientas: [{ herramienta: "Bomba prueba hidrostática", cantidad: 1 }],
      },
      eventos: [],
      observacionesInt: "Cierre exitoso del programa anual 2025.",
      aprobadoPorInt: "interventormecanico01@ingecontrol.com.co",
      aprobadoPorPl2: "rafael.garcia@tecpetrol.com",
      firmaSha256: "f9e8d7c6b5a4321h09j...",
      fechaIntegracion: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "metalicasbrayan@hotmail.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
  ];
  if (rdCount === 0) {
    await localDb.rds.bulkAdd(leerRdsDefault() ?? sampleRds);
  }

  // ===== Actas =====
  const sampleActas: Acta[] = [
    {
      id: SEED_IDS.acta1,
      tenantId: TENANT_LOCAL,
      codigo: "ACTA-OT-094-001-2026-04",
      soId: SEED_IDS.so1,
      periodoInicio: "2026-04-01",
      periodoFin: "2026-04-30",
      estado: "FACTURADA",
      lineas: [
        { tarifaId: "tar-mt-001", descripcion: "Movimiento de tierras", unidad: "m3", cantidad: 4500, precioUnitario: 25, subtotal: 112500 },
        { tarifaId: "tar-dr-001", descripcion: "Construccion drenajes", unidad: "ml", cantidad: 600, precioUnitario: 120, subtotal: 72000 },
        { tarifaId: "tar-pv-001", descripcion: "Pavimento plataforma", unidad: "m2", cantidad: 0, precioUnitario: 45, subtotal: 0 },
      ],
      total: 184500,
      moneda: "USD",
      firmas: [
        { rol: "ADOR", usuario: "contratista@externo.com", fecha: "2026-05-02T10:00:00Z", decision: "APROBADA", observaciones: "Cantidades verificadas" },
        { rol: "INT", usuario: "interventor@externo.com", fecha: "2026-05-03T15:00:00Z", decision: "APROBADA" },
        { rol: "PL2", usuario: "franklin.torres@tecpetrol.com", fecha: "2026-05-04T09:00:00Z", decision: "APROBADA" },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    {
      id: SEED_IDS.acta2,
      tenantId: TENANT_LOCAL,
      codigo: "ACTA-OT-094-001-2026-05",
      soId: SEED_IDS.so1,
      periodoInicio: "2026-05-01",
      periodoFin: "2026-05-31",
      estado: "EN_REVISION_INT",
      lineas: [
        { tarifaId: "tar-mt-001", descripcion: "Movimiento de tierras", unidad: "m3", cantidad: 250, precioUnitario: 25, subtotal: 6250 },
        { tarifaId: "tar-dr-001", descripcion: "Construccion drenajes", unidad: "ml", cantidad: 40, precioUnitario: 120, subtotal: 4800 },
        { tarifaId: "tar-pv-001", descripcion: "Pavimento plataforma", unidad: "m2", cantidad: 0, precioUnitario: 45, subtotal: 0 },
      ],
      total: 11050,
      moneda: "USD",
      firmas: [
        { rol: "ADOR", usuario: "contratista@externo.com", fecha: "2026-06-01T10:00:00Z", decision: "APROBADA", observaciones: "Pendiente avance del 6 al 31 de mayo" },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "contratista@externo.com",
      updatedBy: "contratista@externo.com",
    },
  ];
  // Enriquecer Actas con totalUsd/totalCop/trmCongelada (heredan TRM del AFE de la SO).
  const actasEnriquecidas: Acta[] = sampleActas.map((a) => {
    const so = sampleSos.find((s) => s.id === a.soId);
    const afe = so ? sampleAfes.find((x) => x.id === so.afeId) : null;
    const trm = afe?.trm ?? 0;
    const { montoUsd, montoCop, trmCongelada } = congelarMonto(a.total, a.moneda, trm);
    return { ...a, totalUsd: montoUsd, totalCop: montoCop, trmCongelada };
  });
  if (actaCount === 0) await localDb.actas.bulkAdd(actasEnriquecidas);

  // ===== Contratos Marco =====
  const sampleContratos: ContratoMarco[] = [
    {
      id: SEED_IDS.contrato1,
      tenantId: TENANT_LOCAL,
      // Fase 3 del ADR — contratos per-gerencia (seed CO).
      gerenciaId: "CO",
      codigo: "PO-100501",
      nombre: "Obras Civiles CPO13",
      disciplina: "CIVIL",
      contratistaId: "soforesta",
      contratistaNombre: "Soforesta S.A.S.",
      nit: "900.123.456-7",
      moneda: "USD",
      vigenciaInicio: "2026-01-01",
      vigenciaFin: "2026-12-31",
      montoTecho: 5000000,
      estado: "VIGENTE",
      // Responsables se leen vivos desde Autoridades → Personal (join por empresa).
      responsables: [],
      tarifas: [
        { id: "tar-mt-001", codigo: "1", descripcion: "Movimiento de tierras", unidad: "m3", precio: 25, rendimiento: 250, unidadRendimiento: "m3/dia" },
        { id: "tar-dr-001", codigo: "2", descripcion: "Construccion drenajes", unidad: "ml", precio: 120, rendimiento: 40, unidadRendimiento: "ml/dia" },
        { id: "tar-pv-001", codigo: "3", descripcion: "Pavimento plataforma", unidad: "m2", precio: 45, rendimiento: 175, unidadRendimiento: "m2/dia" },
        { id: "tar-cn-001", codigo: "4", descripcion: "Concreto estructural", unidad: "m3", precio: 180, rendimiento: 30, unidadRendimiento: "m3/dia" },
      ],
      apus: [
        {
          id: "apu-mt-001",
          tarifaItem: "1", // Movimiento de tierras
          items: [
            { subcosto: "Mano de obra", recurso: "Operador retroexcavadora", cantEjecDia: 250, unidad: "h", cantidad: 8, tarifa: 18, rendimiento: 0.032, valorUnit: 5.76 },
            { subcosto: "Equipo", recurso: "Retroexcavadora 320DL", cantEjecDia: 250, unidad: "h", cantidad: 8, tarifa: 65, rendimiento: 0.032, valorUnit: 20.80 },
            { subcosto: "Material", recurso: "Combustible diesel", cantEjecDia: 250, unidad: "gal", cantidad: 18, tarifa: 3.5, rendimiento: 0.072, valorUnit: 0.252 },
          ],
        },
        {
          id: "apu-pv-001",
          tarifaItem: "3", // Pavimento plataforma
          items: [
            { subcosto: "Mano de obra", recurso: "Cuadrilla pavimentacion (3p)", cantEjecDia: 175, unidad: "h", cantidad: 8, tarifa: 22, rendimiento: 0.046, valorUnit: 1.012 },
            { subcosto: "Material", recurso: "Asfalto MDC-2", cantEjecDia: 175, unidad: "ton", cantidad: 12, tarifa: 320, rendimiento: 0.069, valorUnit: 22.08 },
            { subcosto: "Equipo", recurso: "Vibrocompactador 10t", cantEjecDia: 175, unidad: "h", cantidad: 8, tarifa: 45, rendimiento: 0.046, valorUnit: 2.07 },
          ],
        },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.contrato2,
      tenantId: TENANT_LOCAL,
      gerenciaId: "CO",
      codigo: "PO-100502",
      nombre: "Obras Mecánicas Mayores CPO13",
      disciplina: "MECANICA",
      contratistaId: "metalicas-brayan",
      contratistaNombre: "Metálicas Brayan SAS",
      nit: "830.555.123-4",
      moneda: "USD",
      vigenciaInicio: "2026-03-01",
      vigenciaFin: "2026-09-30",
      montoTecho: 2500000,
      estado: "VIGENTE",
      responsables: [],
      tarifas: [
        { id: "tar-tu-001", codigo: "1", descripcion: "Tendido tuberia 4 pulg", unidad: "ml", precio: 85, rendimiento: 60, unidadRendimiento: "ml/dia" },
        { id: "tar-sd-001", codigo: "2", descripcion: "Soldadura tuberia", unidad: "u", precio: 45, rendimiento: 12, unidadRendimiento: "u/dia" },
        { id: "tar-pi-001", codigo: "3", descripcion: "Prueba hidrostatica", unidad: "u", precio: 350, rendimiento: 1, unidadRendimiento: "u/dia" },
      ],
      apus: [
        {
          id: "apu-sd-001",
          tarifaItem: "2", // Soldadura tubería
          items: [
            { subcosto: "Mano de obra", recurso: "Soldador certificado 6G", cantEjecDia: 12, unidad: "h", cantidad: 8, tarifa: 28, rendimiento: 0.667, valorUnit: 18.67 },
            { subcosto: "Mano de obra", recurso: "Ayudante de soldadura", cantEjecDia: 12, unidad: "h", cantidad: 8, tarifa: 14, rendimiento: 0.667, valorUnit: 9.33 },
            { subcosto: "Material", recurso: "Electrodos E6010 + E7018", cantEjecDia: 12, unidad: "kg", cantidad: 1.5, tarifa: 12, rendimiento: 0.125, valorUnit: 1.50 },
          ],
        },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    {
      id: SEED_IDS.contrato3,
      tenantId: TENANT_LOCAL,
      gerenciaId: "CO",
      codigo: "PO-090005",
      nombre: "Obras Eléctricas CPO13",
      disciplina: "ELECTRICA",
      contratistaId: "beoriente",
      contratistaNombre: "Bobinados Electro Oriente S.A.S.",
      nit: "901.456.789-2",
      moneda: "COP",
      vigenciaInicio: "2025-06-01",
      vigenciaFin: "2026-05-31",
      montoTecho: 3200000000,
      estado: "VIGENTE",
      responsables: [
        // fantasmas removidos — Personal vivo en Autoridades
      ],
      tarifas: [
        { id: "tar-tr-001", codigo: "1", descripcion: "Transporte personal (camioneta)", unidad: "km", precio: 3400, rendimiento: 200, unidadRendimiento: "km/dia" },
        { id: "tar-tc-001", codigo: "2", descripcion: "Transporte carga (volqueta)", unidad: "km", precio: 6000, rendimiento: 150, unidadRendimiento: "km/dia" },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    // ===== Contrato 4 — Ingecontrol S.A. (BORRADOR para edición posterior) =====
    // Plantilla mínima generada automáticamente; el ADMIN_PMIS / JEFE completará
    // tarifas, vigencia, monto techo, NIT y responsables. Sirve además como
    // base de pruebas para SOs futuras y como referencia de interventoría
    // contratada como servicio marco.
    {
      id: SEED_IDS.contrato4,
      tenantId: TENANT_LOCAL,
      gerenciaId: "CO",
      codigo: "BORRADOR-INGECONTROL",
      nombre: "Interventoría Técnica",
      disciplina: "INTERVENTORIA",
      contratistaId: "ingecontrol",
      contratistaNombre: "Ingecontrol S.A.",
      nit: "",
      moneda: "USD",
      vigenciaInicio: "2026-01-01",
      vigenciaFin: "2026-12-31",
      montoTecho: 0,
      estado: "BORRADOR",
      responsables: [],
      tarifas: [],
      apus: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "system",
      updatedBy: "system",
    },
    // ===== Contrato MATERIALES (CM-MAT-001) — Ferrematerial CPO ============
    // Soporta el módulo Materiales por CM (MR). PL2 mecánico emite pedidos
    // contra este catálogo; ALMACEN gestiona la entrega; ABAS registra pago.
    {
      id: "seed-contrato-mat-001",
      tenantId: TENANT_LOCAL,
      codigo: "CM-MAT-001",
      nombre: "Suministro de Materiales Mecánicos CPO13",
      tipo: "MATERIALES",
      disciplina: "MECANICA",
      contratistaId: "ferrematerial-cpo",
      contratistaNombre: "Ferrematerial CPO S.A.S.",
      nit: "900123456-7",
      moneda: "USD",
      vigenciaInicio: "2026-01-01",
      vigenciaFin: "2026-12-31",
      montoTecho: 250_000,
      estado: "VIGENTE",
      gerenciaId: "CO",
      tarifas: [
        { id: "tar-mat-001", codigo: "1", descripcion: "Tubería acero al carbono 4\" SCH 80, ASTM A106 Gr B", unidad: "ml", precio: 85 },
        { id: "tar-mat-002", codigo: "2", descripcion: "Codo 90° 4\" SCH 80 acero al carbono", unidad: "u", precio: 45 },
        { id: "tar-mat-003", codigo: "3", descripcion: "Brida slip-on 4\" 150# RF acero al carbono", unidad: "u", precio: 28 },
        { id: "tar-mat-004", codigo: "4", descripcion: "Válvula de bola 4\" 150# acero al carbono trim 316L", unidad: "u", precio: 320 },
        { id: "tar-mat-005", codigo: "5", descripcion: "Tornillería ASTM A193 B7 / A194 2H 5/8\" x 3-1/2\"", unidad: "kg", precio: 12 },
        { id: "tar-mat-006", codigo: "6", descripcion: "Empaque espirometálico 4\" 150#", unidad: "u", precio: 18 },
      ],
      responsables: [
        { rol: "PLANNER", nombre: "Patricia Soler", email: "patricia.soler@ferrematerial.com.co", cargo: "Planeadora" },
        { rol: "ADOR", nombre: "Jorge Quintana", email: "jorge.quintana@ferrematerial.com.co", cargo: "Administrador" },
      ],
      apus: [],
      incrementosIpc: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
  ];
  if (contratoCount === 0) {
    // Prioriza el snapshot guardado por el ADMIN_PMIS (incluye sus ediciones
    // de tarifas, APUs y responsables). Si no hay snapshot, cae al baseline
    // de código (`sampleContratos`) — la red de seguridad de fábrica.
    const snapshot = leerContratosDefault();
    await localDb.contratosMarco.bulkAdd(snapshot ?? sampleContratos);
  }

  // ===== Purchase Requisitions (modelo VD §4.10) — 5 ejemplos cubriendo todos los estados =====
  const samplePrs: Pr[] = [
    // 1) CREADA_NO_APROBADA — recién creada, esperando aprobación de gerencia
    {
      id: "seed-pr-001",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-001",
      afeId: SEED_IDS.afe2,
      costeCodigo: "A111004", // Obras civiles
      concepto: "Materiales para vías de acceso campo Aguada",
      justificacion: "Necesario para iniciar mejoramiento de vías en abril",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "LLI",
      disciplina: "CIVIL",
      solicitadoPor: "franklin.torres@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 240_000,
      moneda: "USD",
      fechaCreacionPr: "2026-04-05",
      fechaLimite: "2026-06-30",
      estado: "CREADA_NO_APROBADA",
      lineas: [
        { id: "pl1-1", numItem: 1, descripcion: "Material granular base", cantidad: 4_000, unidad: "m3", valorUnit: 35, valorTotal: 140_000 },
        { id: "pl1-2", numItem: 2, descripcion: "Geotextil no tejido 200gr", cantidad: 12_000, unidad: "m2", valorUnit: 8.33, valorTotal: 99_960 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "franklin.torres@tecpetrol.com",
    },
    // 2) APROBADA — aprobada por gerencia, lista para arrancar Evaluación Técnica
    {
      id: "seed-pr-002",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-002",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A2100001", // Materiales Eléctricos
      concepto: "Cables y tableros eléctricos baja tensión CT-094",
      justificacion: "Insumos para montaje eléctrico de plataforma",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "LLI",
      disciplina: "ELECTRICA",
      solicitadoPor: "german.bolanos@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 185_000,
      moneda: "USD",
      fechaCreacionPr: "2026-03-01",
      fechaLimite: "2026-06-15",
      fechaAprobacionGerencia: "2026-03-08",
      estado: "APROBADA",
      lineas: [
        { id: "pl2-1", numItem: 1, descripcion: "Cable XLPE 4×4 AWG 600V (rollo 100m)", cantidad: 30, unidad: "rollo", valorUnit: 2_800, valorTotal: 84_000 },
        { id: "pl2-2", numItem: 2, descripcion: "Tablero distribución 12 circuitos", cantidad: 6, unidad: "u", valorUnit: 4_500, valorTotal: 27_000 },
        { id: "pl2-3", numItem: 3, descripcion: "Bandeja portacables 600mm (×3m)", cantidad: 80, unidad: "u", valorUnit: 925, valorTotal: 74_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "german.bolanos@tecpetrol.com",
      updatedBy: "daniel.medina@tecpetrol.com",
    },
    // 3) ET_FAC — en Evaluación Técnica con FAC
    {
      id: "seed-pr-003",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-003",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006", // Montaje Mecánico
      concepto: "Servicios de transporte y logística de equipos pesados",
      tipoSuministro: "SERVICIO_PUNTUAL",
      leadTimeClasif: "SLI",
      disciplina: "MECANICA",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 65_000,
      moneda: "USD",
      fechaCreacionPr: "2026-02-15",
      fechaLimite: "2026-04-30",
      fechaAprobacionGerencia: "2026-02-22",
      fechaInicioEt: "2026-02-25",
      estado: "ET_FAC",
      lineas: [
        { id: "pl3-1", numItem: 1, descripcion: "Transporte retroexcavadora 32 ton (Bogotá → CT-094)", cantidad: 2, unidad: "viaje", valorUnit: 12_000, valorTotal: 24_000 },
        { id: "pl3-2", numItem: 2, descripcion: "Servicio grúas montaje y descarga", cantidad: 5, unidad: "día", valorUnit: 8_200, valorTotal: 41_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "francisco.herrera@tecpetrol.com",
    },
    // 4) EE_ABAS — Evaluación Económica de Abastecimientos
    {
      id: "seed-pr-004",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-004",
      afeId: SEED_IDS.afe2,
      costeCodigo: "80010005", // Estudios topográficos
      concepto: "Estudios topográficos detallados vías Aguada",
      tipoSuministro: "SERVICIO_PUNTUAL",
      leadTimeClasif: "SLI",
      disciplina: "CIVIL",
      solicitadoPor: "franklin.torres@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 80_000,
      moneda: "USD",
      fechaCreacionPr: "2026-02-10",
      fechaLimite: "2026-04-15",
      fechaAprobacionGerencia: "2026-02-15",
      fechaInicioEt: "2026-02-18",
      fechaFinEt: "2026-02-28",
      estado: "EE_ABAS",
      lineas: [
        { id: "pl4-1", numItem: 1, descripcion: "Topografía 12 km (planimetría + altimetría)", cantidad: 12, unidad: "km", valorUnit: 5_500, valorTotal: 66_000 },
        { id: "pl4-2", numItem: 2, descripcion: "Informe técnico y planos AutoCAD", cantidad: 1, unidad: "global", valorUnit: 14_000, valorTotal: 14_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "francisco.herrera@tecpetrol.com",
    },
    // 5) ADJ_TOTAL — adjudicada completa, todas las POs ya emitidas
    {
      id: "seed-pr-005",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-005",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A210005", // Materiales civiles
      concepto: "Tubería y accesorios para plataforma CT-094",
      justificacion: "Material crítico para iniciar fase de obra civil",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "SLI",
      disciplina: "CIVIL",
      solicitadoPor: "daniel.medina@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 270_000,
      moneda: "USD",
      fechaCreacionPr: "2026-01-15",
      fechaLimite: "2026-02-28",
      fechaAprobacionGerencia: "2026-01-22",
      fechaInicioEt: "2026-01-25",
      fechaFinEt: "2026-02-05",
      fechaEmisionPo: "2026-02-10",
      fechaAdjudicacionCompleta: "2026-02-15",
      estado: "ADJ_TOTAL",
      lineas: [
        { id: "pl5-1", numItem: 1, descripcion: "Tubería acero al carbono 4 pulg cedula 40 (×6m)", cantidad: 200, unidad: "u", valorUnit: 450, valorTotal: 90_000 },
        { id: "pl5-2", numItem: 2, descripcion: "Codos 90 grados 4 pulg", cantidad: 100, unidad: "u", valorUnit: 85, valorTotal: 8_500 },
        { id: "pl5-3", numItem: 3, descripcion: "Válvulas de bola 4 pulg", cantidad: 50, unidad: "u", valorUnit: 3_430, valorTotal: 171_500 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "daniel.medina@tecpetrol.com",
      updatedBy: "francisco.herrera@tecpetrol.com",
    },
    // 6) ADJ_TOTAL — Equipos LLI: aprob 05-ene → emisión 15-abr (100d) → ALERTA SLA 90d
    {
      id: "seed-pr-006",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-006",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      concepto: "Bombas centrífugas y compresores planta deshidratación",
      justificacion: "Equipos rotativos críticos para arranque planta CT-094",
      tipoSuministro: "EQUIPO",
      leadTimeClasif: "LLI",
      disciplina: "MECANICA",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "diana.ruiz@tecpetrol.com",
      monto: 480_000,
      moneda: "USD",
      fechaCreacionPr: "2025-12-20",
      fechaLimite: "2026-04-30",
      fechaAprobacionGerencia: "2026-01-05",
      fechaInicioEt: "2026-01-10",
      fechaFinEt: "2026-03-10",
      fechaEmisionPo: "2026-04-15",
      fechaAdjudicacionCompleta: "2026-04-25",
      estado: "ADJ_TOTAL",
      lineas: [
        { id: "pl6-1", numItem: 1, descripcion: "Bomba centrífuga API 610 BB1 250 m3/h", cantidad: 4, unidad: "u", valorUnit: 65_000, valorTotal: 260_000 },
        { id: "pl6-2", numItem: 2, descripcion: "Compresor reciprocante 200 HP", cantidad: 2, unidad: "u", valorUnit: 110_000, valorTotal: 220_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "diana.ruiz@tecpetrol.com",
    },
    // 7) ADJ_TOTAL — Materiales LLI: aprob 10-ene → emisión 26-mar (75d) → OK SLA 90d
    {
      id: "seed-pr-007",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-007",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210005",
      concepto: "Lámina de acero ASTM A516 Gr 70 para tanques de crudo",
      justificacion: "Material de fabricación tanques de almacenamiento Aguada",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "LLI",
      disciplina: "CIVIL",
      solicitadoPor: "franklin.torres@tecpetrol.com",
      comprador: "laura.mendez@tecpetrol.com",
      monto: 320_000,
      moneda: "USD",
      fechaCreacionPr: "2025-12-15",
      fechaLimite: "2026-04-15",
      fechaAprobacionGerencia: "2026-01-10",
      fechaInicioEt: "2026-01-15",
      fechaFinEt: "2026-02-25",
      fechaEmisionPo: "2026-03-26",
      fechaAdjudicacionCompleta: "2026-04-02",
      estado: "ADJ_TOTAL",
      lineas: [
        { id: "pl7-1", numItem: 1, descripcion: "Lámina acero A516 Gr 70 espesor 1/2'' (×2.4m × 12m)", cantidad: 80, unidad: "u", valorUnit: 2_900, valorTotal: 232_000 },
        { id: "pl7-2", numItem: 2, descripcion: "Perfil estructural HEB 240 (×12m)", cantidad: 40, unidad: "u", valorUnit: 2_200, valorTotal: 88_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 8) ADJ_PARCIAL — Equipos SLI: aprob 25-feb → emisión 23-abr (57d) → OK SLA 60d
    {
      id: "seed-pr-008",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-008",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A210002",
      concepto: "Válvulas y accesorios skid de bombeo",
      tipoSuministro: "EQUIPO",
      leadTimeClasif: "SLI",
      disciplina: "MECANICA",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "diana.ruiz@tecpetrol.com",
      monto: 220_000,
      moneda: "USD",
      fechaCreacionPr: "2026-02-10",
      fechaLimite: "2026-05-15",
      fechaAprobacionGerencia: "2026-02-25",
      fechaInicioEt: "2026-03-01",
      fechaFinEt: "2026-04-15",
      fechaEmisionPo: "2026-04-23",
      estado: "ADJ_PARCIAL",
      lineas: [
        { id: "pl8-1", numItem: 1, descripcion: "Válvula globo 6'' clase 300 acero", cantidad: 12, unidad: "u", valorUnit: 8_500, valorTotal: 102_000 },
        { id: "pl8-2", numItem: 2, descripcion: "Válvula check 4'' clase 300", cantidad: 18, unidad: "u", valorUnit: 4_200, valorTotal: 75_600 },
        { id: "pl8-3", numItem: 3, descripcion: "Bridas slip-on 6'' clase 300", cantidad: 60, unidad: "u", valorUnit: 707, valorTotal: 42_400 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "diana.ruiz@tecpetrol.com",
    },
    // 9) ADJ_PARCIAL — Contrato Marco: aprob 10-ene → emisión 05-may (115d) → EXCEDIDO SLA 90d
    {
      id: "seed-pr-009",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-009",
      afeId: SEED_IDS.afe8,
      costeCodigo: "80040003",
      concepto: "Contrato marco transporte de carga pesada 2026",
      justificacion: "Servicios de movilización de equipos pesados a sitios de obra",
      tipoSuministro: "CONTRATO_MARCO",
      leadTimeClasif: "SLI",
      disciplina: "GENERAL",
      solicitadoPor: "daniel.medina@tecpetrol.com",
      comprador: "jose.salazar@tecpetrol.com",
      monto: 600_000,
      moneda: "USD",
      fechaCreacionPr: "2025-12-15",
      fechaLimite: "2026-05-31",
      fechaAprobacionGerencia: "2026-01-10",
      fechaInicioEt: "2026-01-15",
      fechaFinEt: "2026-04-25",
      fechaEmisionPo: "2026-05-05",
      estado: "ADJ_PARCIAL",
      lineas: [
        { id: "pl9-1", numItem: 1, descripcion: "Transporte equipos > 30 ton (Bogotá → Casanare)", cantidad: 20, unidad: "viaje", valorUnit: 18_000, valorTotal: 360_000 },
        { id: "pl9-2", numItem: 2, descripcion: "Servicio grúa móvil 100 ton", cantidad: 30, unidad: "día", valorUnit: 8_000, valorTotal: 240_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "daniel.medina@tecpetrol.com",
      updatedBy: "jose.salazar@tecpetrol.com",
    },
    // 10) ADJ_TOTAL — Materiales SLI: aprob 20-ene → emisión 25-mar (64d) → ALERTA SLA 60d
    {
      id: "seed-pr-010",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-010",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A2100001",
      concepto: "Tableros eléctricos prefabricados CT-094 (segundo lote)",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "SLI",
      disciplina: "ELECTRICA",
      solicitadoPor: "german.bolanos@tecpetrol.com",
      comprador: "laura.mendez@tecpetrol.com",
      monto: 185_000,
      moneda: "USD",
      fechaCreacionPr: "2026-01-08",
      fechaLimite: "2026-04-30",
      fechaAprobacionGerencia: "2026-01-20",
      fechaInicioEt: "2026-01-23",
      fechaFinEt: "2026-03-05",
      fechaEmisionPo: "2026-03-25",
      fechaAdjudicacionCompleta: "2026-04-05",
      estado: "ADJ_TOTAL",
      lineas: [
        { id: "pl10-1", numItem: 1, descripcion: "Tablero distribución 480V 24 circuitos", cantidad: 4, unidad: "u", valorUnit: 32_000, valorTotal: 128_000 },
        { id: "pl10-2", numItem: 2, descripcion: "CCM tipo Pringle 6 cucharas", cantidad: 1, unidad: "u", valorUnit: 57_000, valorTotal: 57_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "german.bolanos@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 11) ADJ_PARCIAL — Materiales LLI: aprob 20-ene → emisión 05-may (105d) → ALERTA SLA 90d
    {
      id: "seed-pr-011",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-011",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      concepto: "Tubería piping API 5L Gr B sch 80 para tanques",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "LLI",
      disciplina: "CIVIL",
      solicitadoPor: "franklin.torres@tecpetrol.com",
      comprador: "laura.mendez@tecpetrol.com",
      monto: 410_000,
      moneda: "USD",
      fechaCreacionPr: "2026-01-05",
      fechaLimite: "2026-05-31",
      fechaAprobacionGerencia: "2026-01-20",
      fechaInicioEt: "2026-01-25",
      fechaFinEt: "2026-04-15",
      fechaEmisionPo: "2026-05-05",
      estado: "ADJ_PARCIAL",
      lineas: [
        { id: "pl11-1", numItem: 1, descripcion: "Tubería API 5L Gr B 8'' sch 80 (×6m)", cantidad: 250, unidad: "u", valorUnit: 1_400, valorTotal: 350_000 },
        { id: "pl11-2", numItem: 2, descripcion: "Codos 90° 8'' sch 80", cantidad: 80, unidad: "u", valorUnit: 750, valorTotal: 60_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 12) EE_ABAS — Materiales SLI: aprob 01-feb (97d) → EXCEDIDO SLA 60d
    {
      id: "seed-pr-012",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-012",
      afeId: SEED_IDS.afe6,
      costeCodigo: "A210003",
      concepto: "Instrumentación de campo (transmisores presión, temperatura, nivel)",
      tipoSuministro: "MATERIAL",
      leadTimeClasif: "SLI",
      disciplina: "INSTRUMENTACION",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "diana.ruiz@tecpetrol.com",
      monto: 350_000,
      moneda: "USD",
      fechaCreacionPr: "2026-01-15",
      fechaLimite: "2026-05-30",
      fechaAprobacionGerencia: "2026-02-01",
      fechaInicioEt: "2026-02-08",
      fechaFinEt: "2026-03-25",
      estado: "EE_ABAS",
      lineas: [
        { id: "pl12-1", numItem: 1, descripcion: "Transmisor presión Rosemount 3051S", cantidad: 60, unidad: "u", valorUnit: 3_200, valorTotal: 192_000 },
        { id: "pl12-2", numItem: 2, descripcion: "Transmisor temperatura RTD pt100", cantidad: 80, unidad: "u", valorUnit: 1_400, valorTotal: 112_000 },
        { id: "pl12-3", numItem: 3, descripcion: "Transmisor nivel radar 6m", cantidad: 16, unidad: "u", valorUnit: 2_875, valorTotal: 46_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "diana.ruiz@tecpetrol.com",
    },
    // 13) ET_FAC — Equipos SLI: aprob 19-abr (20d) → OK SLA 60d (en curso)
    {
      id: "seed-pr-013",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-013",
      afeId: SEED_IDS.afe6,
      costeCodigo: "A210003",
      concepto: "Servidores SCADA y PLC redundantes",
      tipoSuministro: "EQUIPO",
      leadTimeClasif: "SLI",
      disciplina: "INSTRUMENTACION",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "diana.ruiz@tecpetrol.com",
      monto: 280_000,
      moneda: "USD",
      fechaCreacionPr: "2026-04-10",
      fechaLimite: "2026-07-15",
      fechaAprobacionGerencia: "2026-04-19",
      fechaInicioEt: "2026-04-22",
      estado: "ET_FAC",
      lineas: [
        { id: "pl13-1", numItem: 1, descripcion: "Servidor SCADA HP DL380 redundante (par)", cantidad: 1, unidad: "par", valorUnit: 95_000, valorTotal: 95_000 },
        { id: "pl13-2", numItem: 2, descripcion: "PLC Allen Bradley ControlLogix L82E", cantidad: 4, unidad: "u", valorUnit: 28_000, valorTotal: 112_000 },
        { id: "pl13-3", numItem: 3, descripcion: "Switches industriales 24 puertos gigabit", cantidad: 12, unidad: "u", valorUnit: 6_083, valorTotal: 73_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "diana.ruiz@tecpetrol.com",
    },
    // 14) ET_FAC — Equipos LLI: aprob 25-ene (104d) → ALERTA SLA 90d (en curso)
    {
      id: "seed-pr-014",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-014",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      concepto: "Turbo-expansores planta deshidratación (importación)",
      justificacion: "Equipos críticos de fabricación especializada — plazo importación >120 días",
      tipoSuministro: "EQUIPO",
      leadTimeClasif: "LLI",
      disciplina: "MECANICA",
      solicitadoPor: "rafael.garcia@tecpetrol.com",
      comprador: "francisco.herrera@tecpetrol.com",
      monto: 720_000,
      moneda: "USD",
      fechaCreacionPr: "2026-01-12",
      fechaLimite: "2026-09-30",
      fechaAprobacionGerencia: "2026-01-25",
      fechaInicioEt: "2026-02-01",
      estado: "ET_FAC",
      lineas: [
        { id: "pl14-1", numItem: 1, descripcion: "Turbo-expansor centrífugo 800 HP", cantidad: 2, unidad: "u", valorUnit: 320_000, valorTotal: 640_000 },
        { id: "pl14-2", numItem: 2, descripcion: "Sistema lubricación + monitoreo vibración", cantidad: 2, unidad: "set", valorUnit: 40_000, valorTotal: 80_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "francisco.herrera@tecpetrol.com",
    },
    // 15) APROBADA — Servicio SLI: aprob 29-abr (10d) → OK SLA 60d (recién aprobada)
    {
      id: "seed-pr-015",
      tenantId: TENANT_LOCAL,
      codigo: "PR-2026-015",
      afeId: SEED_IDS.afe7,
      costeCodigo: "71100003",
      concepto: "Interventoría obras civiles segundo semestre 2026",
      tipoSuministro: "SERVICIO_PUNTUAL",
      leadTimeClasif: "SLI",
      disciplina: "INTERVENTORIA",
      solicitadoPor: "franklin.torres@tecpetrol.com",
      comprador: "jose.salazar@tecpetrol.com",
      monto: 150_000,
      moneda: "USD",
      fechaCreacionPr: "2026-04-22",
      fechaLimite: "2026-06-30",
      fechaAprobacionGerencia: "2026-04-29",
      estado: "APROBADA",
      lineas: [
        { id: "pl15-1", numItem: 1, descripcion: "Interventor residente civil (6 meses)", cantidad: 6, unidad: "mes", valorUnit: 18_500, valorTotal: 111_000 },
        { id: "pl15-2", numItem: 2, descripcion: "Interventor HSE compartido (6 meses)", cantidad: 6, unidad: "mes", valorUnit: 6_500, valorTotal: 39_000 },
      ],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "franklin.torres@tecpetrol.com",
      updatedBy: "jose.salazar@tecpetrol.com",
    },
  ];
  // Enriquecer PRs con montoUsd/montoCop/trmCongelada usando la TRM del AFE padre.
  const prsEnriquecidas: Pr[] = samplePrs.map((p) => {
    const afe = sampleAfes.find((a) => a.id === p.afeId);
    return { ...p, ...congelarMonto(p.monto, p.moneda, afe?.trm ?? 0) };
  });
  if (prCount === 0) await localDb.prs.bulkAdd(prsEnriquecidas);

  // ===== Purchase Orders (modelo VD §4.11) — 8 ejemplos, todas asociadas a PRs ADJ_TOTAL/ADJ_PARCIAL =====
  // Cubren los 6 estados PO: EMITIDA · EN_ESPERA_ENTREGA · ENTREGADA · PAGADA · VENCIDA · CANCELADA.
  // El campo `lineas: []` se rellena por defecto en cada PO del seed — las POs
  // de demo se persisten con el sub-detalle vacío para no inflar el archivo;
  // el monto agregado de la PO permanece como histórico hasta que un editor
  // del PMIS digite las líneas reales en /po/:id.
  const samplePosBase: Array<Omit<Po, "lineas">> = [
    // 1) PAGADA — derivada de PR-005 (ADJ_TOTAL $270K) — entrega anticipada y pagada
    {
      id: "seed-po-001",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-101",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A210005",
      prId: "seed-pr-005",
      descripcion: "Tubería acero + codos + válvulas para plataforma CT-094",
      proveedorId: "suministros-llano",
      proveedorNombre: "Suministros Industriales del Llano S.A.S.",
      proveedorContacto: "Pedro Aguilar — paguilar@suministrosllano.co",
      fechaEmision: "2026-02-15",
      fechaVigenciaFin: "2026-04-30",
      plazoEntregaDias: 25,
      fechaEntregaCalc: "2026-03-12",
      fechaEntregaReal: "2026-03-10",
      fechaPago: "2026-03-25",
      valorPago: 270_000,
      monto: 270_000,
      moneda: "USD",
      tipoSuministro: "MATERIAL",
      estado: "PAGADA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-101.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "francisco.herrera@tecpetrol.com",
      updatedBy: "francisco.herrera@tecpetrol.com",
    },
    // 2) ENTREGADA — derivada de PR-006 (ADJ_TOTAL $480K) — equipos rotativos entregados, falta pago
    {
      id: "seed-po-002",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-102",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A111006",
      prId: "seed-pr-006",
      descripcion: "Bombas centrífugas API 610 + compresores reciprocantes 200 HP",
      proveedorId: "metalicas-brayan",
      proveedorNombre: "Metálicas Brayan S.A.S.",
      proveedorContacto: "Yahahiny Betancourt — metalicasbrayan@hotmail.com",
      fechaEmision: "2026-04-15",
      fechaVigenciaFin: "2026-07-31",
      plazoEntregaDias: 18,
      fechaEntregaCalc: "2026-05-03",
      fechaEntregaReal: "2026-05-02",
      monto: 480_000,
      moneda: "USD",
      tipoSuministro: "EQUIPO",
      estado: "ENTREGADA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-102.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "diana.ruiz@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
    },
    // 3) ENTREGADA — derivada de PR-007 (ADJ_TOTAL $320K) — parcial 1 de 2: lámina acero ya entregada
    {
      id: "seed-po-003",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-103",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210005",
      prId: "seed-pr-007",
      descripcion: "Lámina acero ASTM A516 Gr 70 — fabricación tanques Aguada",
      proveedorId: "aceros-cesar",
      proveedorNombre: "Aceros del Cesar S.A.S.",
      proveedorContacto: "Mauricio Salinas — msalinas@acerosdelcesar.co",
      fechaEmision: "2026-03-26",
      fechaVigenciaFin: "2026-06-30",
      plazoEntregaDias: 25,
      fechaEntregaCalc: "2026-04-20",
      fechaEntregaReal: "2026-04-22", // 2 días de atraso aceptable
      monto: 232_000,
      moneda: "USD",
      tipoSuministro: "MATERIAL",
      estado: "ENTREGADA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-103.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "laura.mendez@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 4) VENCIDA — derivada de PR-007 (ADJ_TOTAL $320K) — parcial 2 de 2: perfilería sin entregar
    {
      id: "seed-po-004",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-104",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210005",
      prId: "seed-pr-007",
      descripcion: "Perfil estructural HEB 240 — refuerzos tanques Aguada",
      proveedorId: "perfileria-col",
      proveedorNombre: "Perfilería Industrial Colombia S.A.S.",
      proveedorContacto: "Andrés Caicedo — acaicedo@perfileriacol.co",
      fechaEmision: "2026-04-05",
      fechaVigenciaFin: "2026-06-30",
      plazoEntregaDias: 30,
      fechaEntregaCalc: "2026-05-05",
      monto: 88_000,
      moneda: "USD",
      tipoSuministro: "MATERIAL",
      estado: "VENCIDA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-104.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "laura.mendez@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 5) EN_ESPERA_ENTREGA — derivada de PR-008 (ADJ_PARCIAL $220K) — parcial 1 de 2: válvulas
    {
      id: "seed-po-005",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-105",
      afeId: SEED_IDS.afe4,
      costeCodigo: "A210002",
      prId: "seed-pr-008",
      descripcion: "Válvulas globo 6'' clase 300 + bridas slip-on para skid de bombeo",
      proveedorId: "suministros-llano",
      proveedorNombre: "Suministros Industriales del Llano S.A.S.",
      proveedorContacto: "Pedro Aguilar — paguilar@suministrosllano.co",
      fechaEmision: "2026-04-23",
      fechaVigenciaFin: "2026-06-30",
      plazoEntregaDias: 30,
      fechaEntregaCalc: "2026-05-23",
      monto: 130_000,
      moneda: "USD",
      tipoSuministro: "EQUIPO",
      estado: "EN_ESPERA_ENTREGA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-105.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "diana.ruiz@tecpetrol.com",
      updatedBy: "diana.ruiz@tecpetrol.com",
    },
    // 6) EMITIDA — derivada de PR-009 (ADJ_PARCIAL CONTRATO_MARCO $600K) — primera fase recién emitida
    {
      id: "seed-po-006",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-106",
      afeId: SEED_IDS.afe8,
      costeCodigo: "80040003",
      prId: "seed-pr-009",
      descripcion: "Transporte equipos pesados Bogotá → CT-094 (1ª fase contrato marco)",
      proveedorId: "logistica-llano",
      proveedorNombre: "Logística del Llano S.A.S.",
      proveedorContacto: "Carolina Ramírez — cramirez@logisticallano.co",
      fechaEmision: "2026-05-05",
      fechaVigenciaFin: "2026-09-30",
      plazoEntregaDias: 60,
      fechaEntregaCalc: "2026-07-04",
      monto: 360_000,
      moneda: "USD",
      tipoSuministro: "CONTRATO_MARCO",
      estado: "EMITIDA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-106.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "jose.salazar@tecpetrol.com",
      updatedBy: "jose.salazar@tecpetrol.com",
    },
    // 7) EN_ESPERA_ENTREGA — derivada de PR-010 (ADJ_TOTAL $185K) — tableros prefabricados
    {
      id: "seed-po-007",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-107",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A2100001",
      prId: "seed-pr-010",
      descripcion: "Tableros distribución 480V + CCM Pringle (segundo lote CT-094)",
      proveedorId: "beoriente",
      proveedorNombre: "Bobinados Electro Oriente S.A.S.",
      proveedorContacto: "Hollman Lagos — proyectos@beoriente.com",
      fechaEmision: "2026-03-25",
      fechaVigenciaFin: "2026-06-30",
      plazoEntregaDias: 45,
      fechaEntregaCalc: "2026-05-09",
      monto: 185_000,
      moneda: "USD",
      tipoSuministro: "MATERIAL",
      estado: "EN_ESPERA_ENTREGA",
      adjuntoUrl: "https://sharepoint/sitio/PO-2026-107.pdf",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "laura.mendez@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
    // 8) CANCELADA — derivada de PR-011 (ADJ_PARCIAL $410K) — cancelada por errores de especificación
    {
      id: "seed-po-008",
      tenantId: TENANT_LOCAL,
      codigo: "PO-2026-108",
      afeId: SEED_IDS.afe5,
      costeCodigo: "A210002",
      prId: "seed-pr-011",
      descripcion: "Tubería piping API 5L Gr B 8'' sch 80 — primer lote tanques Aguada",
      proveedorId: "suministros-llano",
      proveedorNombre: "Suministros Industriales del Llano S.A.S.",
      proveedorContacto: "Pedro Aguilar — paguilar@suministrosllano.co",
      fechaEmision: "2026-05-05",
      fechaVigenciaFin: "2026-08-31",
      plazoEntregaDias: 30,
      fechaEntregaCalc: "2026-06-04",
      monto: 350_000,
      moneda: "USD",
      tipoSuministro: "MATERIAL",
      estado: "CANCELADA",
      motivoCancelacion: "Errores en especificación del scheduling — se reemitirá con scheduling 80 corregido",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "laura.mendez@tecpetrol.com",
      updatedBy: "laura.mendez@tecpetrol.com",
    },
  ];
  // Enriquecer POs con montoUsd/montoCop/trmCongelada usando la TRM del AFE
  // padre, y agregar `lineas: []` (las POs del seed se cargan sin sub-detalle
  // — el monto agregado actúa como histórico).
  const samplePos: Po[] = samplePosBase.map((p) => ({ ...p, lineas: [] }));
  const posEnriquecidas: Po[] = samplePos.map((p) => {
    const afe = sampleAfes.find((a) => a.id === p.afeId);
    return { ...p, ...congelarMonto(p.monto, p.moneda, afe?.trm ?? 0) };
  });
  if (poCount === 0) await localDb.pos.bulkAdd(posEnriquecidas);

  // ===== Material Requisitions (MR) — pedidos contra CM MATERIALES ========
  // Ejemplo: MR-2026-001 ya EMITIDA del PL2 mecánico contra el CM CM-MAT-001
  // (Ferrematerial CPO). Hereda gerencia vía AFE→Budget. El AFE asociado es
  // el mecánico (afe1 — CT-094) para que la disciplina cuadre.
  const sampleMrs: Mr[] = [
    {
      id: "seed-mr-001",
      tenantId: TENANT_LOCAL,
      codigo: "MR-2026-001",
      afeId: SEED_IDS.afe1,
      costeCodigo: "A111006",
      contratoMarcoId: "seed-contrato-mat-001",
      proveedorId: "ferrematerial-cpo",
      proveedorNombre: "Ferrematerial CPO S.A.S.",
      descripcion: "Materiales tubería pad CT-094 - lote inicial",
      fechaEmision: "2026-04-15",
      fechaVigenciaFin: "2026-12-31",
      plazoEntregaDias: 15,
      fechaEntregaCalc: "2026-04-30",
      monto: 5_650,
      moneda: "USD",
      montoUsd: 5_650,
      montoCop: 5_650 * 4200,
      trmCongelada: 4200,
      lineas: [
        { id: "ml-001-1", numItem: 1, tarifaId: "tar-mat-001", codigoTarifa: "1", descripcion: "Tubería acero al carbono 4\" SCH 80, ASTM A106 Gr B", unidad: "ml", cantidad: 50, valorUnit: 85, valorTotal: 4250 },
        { id: "ml-001-2", numItem: 2, tarifaId: "tar-mat-002", codigoTarifa: "2", descripcion: "Codo 90° 4\" SCH 80 acero al carbono", unidad: "u", cantidad: 20, valorUnit: 45, valorTotal: 900 },
        { id: "ml-001-3", numItem: 3, tarifaId: "tar-mat-005", codigoTarifa: "5", descripcion: "Tornillería ASTM A193 B7 / A194 2H 5/8\" x 3-1/2\"", unidad: "kg", cantidad: 50, valorUnit: 12, valorTotal: 600 },
      ],
      estado: "EMITIDA",
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: "rafael.garcia@tecpetrol.com",
      updatedBy: "rafael.garcia@tecpetrol.com",
      historial: [
        {
          fecha: now,
          estadoAnterior: "BORRADOR",
          estadoNuevo: "EMITIDA",
          usuario: "rafael.garcia@tecpetrol.com",
        },
      ],
    },
  ];
  if (mrCount === 0) await localDb.mrs.bulkPut(sampleMrs);

  // Una vez cargados SOs y AFEs, asignamos códigos humanos a los eventos
  // (los del seed no los traen para evitar duplicar info en la fuente).
  await backfillCodigosEventos();
  // Sintetizamos historiales de PR/PO/SO para que la pestaña "Historial"
  // de cada detalle muestre transiciones razonables en lugar de "vacío".
  await backfillHistorialEntidades();

  // ═══════════════════════════════════════════════════════════════════
  // Seeds satélite — integración de módulos que históricamente vivían
  // como "rueda suelta" con su propio `ensureSeed`. Antes solo corrían
  // cuando el usuario navegaba a la página de ese módulo, lo que dejaba
  // las pestañas Riesgos / Stakeholders / Beneficios vacías inmediatamente
  // después de un Restablecer base. Forzamos su ejecución acá para que
  // un reset deje TODO el PMIS poblado, no solo lo que el seed central
  // conoce de primera mano.
  // ═══════════════════════════════════════════════════════════════════
  try {
    const cantRiesgos = await ensureSeedRiesgos(TENANT_LOCAL, "system@seed");
    if (cantRiesgos > 0) {
      console.info(`[seed satélite] riesgos: ${cantRiesgos} entradas sembradas.`);
    }
  } catch (e) {
    console.warn("[seed satélite] riesgos falló:", e);
  }
  try {
    const cantStakeholders = await ensureSeedStakeholders(
      TENANT_LOCAL,
      "system@seed",
    );
    if (cantStakeholders > 0) {
      console.info(
        `[seed satélite] stakeholders: ${cantStakeholders} entradas sembradas.`,
      );
    }
  } catch (e) {
    console.warn("[seed satélite] stakeholders falló:", e);
  }
  try {
    const repos = buildRepos(TENANT_LOCAL);
    const cantConfig = await repos.notificacionConfig.ensureSeed("system@seed");
    if (cantConfig > 0) {
      console.info(
        `[seed satélite] configuracionesNotificacion: ${cantConfig} reglas sembradas.`,
      );
    }
  } catch (e) {
    console.warn("[seed satélite] configuracionesNotificacion falló:", e);
  }
  try {
    const repos = buildRepos(TENANT_LOCAL);
    const cantListas = await repos.listaDistribucion.ensureSeed("system@seed");
    if (cantListas > 0) {
      console.info(
        `[seed satélite] listasDistribucion: ${cantListas} listas sembradas.`,
      );
    }
  } catch (e) {
    console.warn("[seed satélite] listasDistribucion falló:", e);
  }

  console.info(
    `[Dexie] Seed Capa 2 completo. ` +
      `Budgets: ${budgetCount === 0 ? sampleBudgets.length : 0}, ` +
      `Costes catalogo: ${costeCount === 0 ? sampleCostes.length : 0}, ` +
      `AFEs: ${afeCount === 0 ? sampleAfes.length : 0}, ` +
      `SOs: ${soCount === 0 ? sampleSos.length : 0}, ` +
      `Tareas: ${tareaCount === 0 ? sampleTareas.length : 0}, ` +
      `Usuarios: ${usuarioCount === 0 ? sampleUsuarios.length : 0}, ` +
      `Eventos: ${eventoCount === 0 ? sampleEventos.length : 0}, ` +
      `RDs: ${rdCount === 0 ? sampleRds.length : 0}, ` +
      `Actas: ${actaCount === 0 ? sampleActas.length : 0}, ` +
      `Contratos: ${contratoCount === 0 ? sampleContratos.length : 0}, ` +
      `PRs: ${prCount === 0 ? samplePrs.length : 0}, ` +
      `POs: ${poCount === 0 ? samplePos.length : 0}, ` +
      `MRs: ${mrCount === 0 ? sampleMrs.length : 0}.`,
  );
}
